// client/src/pages/InventoryPage.tsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import Pagination from '../components/ui/Pagination';
import Button from '../components/ui/Button';
import StatusBadge from '../components/StatusBadge';
import SearchInput from '../components/ui/SearchInput';
import { Plus } from 'lucide-react';
import {
  allocateSplitToVessels,
  breakCase,
  clearHold,
  confirmHold,
  createAndApplyInventoryMovement,
  createInventoryItem,
  deleteInventoryItem,
  pullRetained,
  setHold,
  getInventoryFacets,
  getInventoryItems,
  getInventoryMovements,
  getItemCasePlan,
  getItemStockLayers,
  getVesselAllocationBreakdown,
  updateInventoryItem,
  CreateInventoryItemData,
  InventoryItem,
  StockLayerAvailability,
  StockLayerDto,
  StockLayerReconciliation,
  InventoryDivision,
  CasePlanResponse,
  CasePickPlan,
  StockCaseDto,
  VesselSplitRow,
} from '../lib/inventoryApi';
import { uploadVendorInvoiceSingle } from '../lib/vendorInvoiceApi';
import PaymentOnReceiptFields from '../features/vendorInvoices/PaymentOnReceiptFields';
import { checkPaymentDraft, EMPTY_PAYMENT_DRAFT, type PaymentDraft } from '../features/vendorInvoices/paymentOnReceiptForm';
import { toCsv } from '../utils/csv';
import { formatDate as fmtDate, storedDay } from '../utils/dateFormat';
// The canonical client num: finite number, or null for missing/blank/unparseable.
// Missing is not zero, and on this screen the difference is the difference
// between "no cases in the warehouse" and "nothing records a case count".
import { num } from '../utils/invoiceLineMath';
// Mass constants from their owner (docs/BUSINESS_RULES.md). This file held a
// local KG_PER_LB / KG_PER_OZ pair, which is the duplicated-constant shape the
// golden rule names — and the copy that would have gone on writing avoirdupois
// kilograms into uom.weightPerLevel1 after the owner moved to USC's 2.2.
import { KG_PER_UNIT } from '../utils/packConversion';
// The house date reader: refuses 31/02 rather than rolling it into March (USC-BUG-0041).
import { toInvoiceDate } from '../utils/paymentTerms';
// The unit vocabulary's own answer for a pack/piece NAME. The CSV upload used to
// scrape one out of free text with a private regex that needed a leading digit
// (TECH_DEBT #142); `canonicalUnit` is the same first reading
// `vendorInvoiceInventorySync.buildUom` makes for this exact field server-side.
import { canonicalUnit, isDimensionUnit } from '../utils/unitVocabulary';
// The money-label owner (BUSINESS_RULES#quotation-screen-money-labels): "TT$", never a bare "$".
import { formatCurrency } from '../utils/priceUtils';
import DateInput from "../components/DateInput";

const inventoryInfoContent = (isPantry: boolean) => (
  <div className="flex flex-col gap-3">
    <p>
      {isPantry
        ? 'View the pantry items synced from Yelo in a single table.'
        : 'View the Ship Chandlery items, added manually or from vendor invoices, in a single table.'}
    </p>
    <p className="font-medium">From this section, you can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Search and filter items</li>
      <li>Export the inventory table as CSV or PDF</li>
      {isPantry ? (
        <li>Pantry items come from Yelo; adding or uploading items is switched off while pantry is on hold</li>
      ) : (
        <li>Add items manually or upload items using a CSV or PDF file</li>
      )}
    </ul>
    <p className="text-sm mt-2">
      <strong>Note:</strong> Expiry dates must be added manually to receive notifications for each item.
    </p>
  </div>
);

/* ----------------------------------------------------------
   Inline SVG icons (no react-icons dependency)
---------------------------------------------------------- */
const IconEdit = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const IconTrash = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IconX = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconFilter = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 3H2l8 9v7l4 2v-9l8-9z" />
  </svg>
);

/* ----------------------------------------------------------
   Helpers
---------------------------------------------------------- */
const formatDate = (iso?: string | null) => fmtDate(iso, '-');

// The Item Price column is headed "(TT)", so a figure is TT$: through the label owner, not a
// private `$${n.toFixed(2)}` that printed a bare "$" under that heading (lane 5, V4). Shown as
// stored, rounded to the cent, never ceiled. A blank tier (unknown pack count, D102.11) is the
// owner's "—" for a missing amount (D104.30), not TT$0.00.
const formatMoney = (v: any) => formatCurrency(v == null ? null : Number(v), 'TTD', { ceil: false });

const sourceLabel = (s?: string) => {
  const t = String(s || 'manual').toLowerCase();
  if (t === 'invoice') return 'Invoice';
  if (t === 'pantry') return 'Pantry';
  return 'Manual';
};

// An expiry is a calendar day the server keeps as UTC midnight of that day (the box's
// `toInvoiceDate` / `new Date('yyyy-mm-dd')` write it so). The day is therefore its UTC day:
// read in local time, 20/10/2026 is the evening of the 19th in Trinidad (D109.6 review).
const inputDateValue = (iso?: string | null) => storedDay(iso) ?? '';

// Every PRINT of a stored expiry goes through the same day as the box, then the date owner.
const formatExpiry = (iso?: string | null) => fmtDate(inputDateValue(iso), '-');

const getAvailableQty = (it: InventoryItem) => {
  const stock = (it as any)?.stock || {};
  const united = stock?.united || {};
  const pantry = stock?.pantry || {};
  const u1 = Number(united.level1Qty || 0);
  const p1 = Number(pantry.level1Qty || 0);
  return u1 + p1;
};

/**
 * TECH_DEBT #50. The audit modal showed ONE of the two numbers a stock row holds
 * and no unit, so a correct receipt read as a wrong one.
 *
 * The complaint was "why does it say 24 when the invoice is for 8", on prod row
 * 6a83a775b9593c6f045095fd (Magnum DUET Almond 8/3pk/8.62oz, HADCO). Both
 * figures were right: two invoices booked 1 case (8 units) and 2 cases (16
 * units), so `stock.united` is `{level2Qty: 3, level1Qty: 24}`. The modal
 * rendered `level1Qty` alone, while the Quantity column on the paper is the CASE
 * count, so there was nothing on screen that matched the invoice.
 *
 * Worse, the level-1 name on that row is "OZ", taken from the `8.62OZ` in the
 * size text, so the layer's "8" read as a weight when it is a count of 8. That
 * is a separate defect in what INGEST stores (see #50's SETTLED block); showing
 * the case count beside it is what makes the row readable either way, which is
 * why both levels are always rendered rather than trusting the level-1 name.
 */
const getStockLevels = (it: InventoryItem) => {
  const stock = (it as any)?.stock || {};
  const united = stock?.united || {};
  const pantry = stock?.pantry || {};
  // Null only when NEITHER location records the level at all. A location with no
  // entry for it holds none of it, which is a real zero and adds nothing to the
  // total; a level nothing anywhere records is UNKNOWN, and `0 CASE` is not what
  // unknown says on a screen someone reconciles against a paper invoice.
  const total = (key: 'level1Qty' | 'level2Qty') => {
    const u = num((united as any)[key]);
    const p = num((pantry as any)[key]);
    if (u === null && p === null) return null;
    return (u ?? 0) + (p ?? 0);
  };
  return { level1: total('level1Qty'), level2: total('level2Qty') };
};

type PackUom = { level1Name?: string | null; level2Name?: string | null; level1PerLevel2?: number | null };

// One vessel in the "Allocate to Vessels" editor. `caseRefs` (the boxes it ships,
// catch-weight goods only) and `plan` (the suggestion they came from) are facts
// about THIS row, so they travel with it.
type SplitRow = {
  id: number;
  vessel: string;
  qty: string;
  caseRefs?: Array<{ layerId: string; seq: number }>;
  plan?: CasePickPlan | null;
};
let splitRowSeq = 0;
const newSplitRow = (): SplitRow => ({ id: ++splitRowSeq, vessel: '', qty: '' });
/**
 * The row with its boxes and suggestion dropped: they were worked out against stock that may have moved.
 * A row that had boxes loses its quantity too: that number was the boxes' weight, never typed by the
 * user, and sent as a plain quantity it would let the server drain whichever boxes expire first. The
 * blank row is refused at submit by name.
 */
const withoutCases = ({ id, vessel, qty, caseRefs }: SplitRow): SplitRow => ({
  id,
  vessel,
  qty: caseRefs?.length ? '' : qty,
});

/**
 * A pack that is itself a MEASURE (LBS, KG, L...: the unit vocabulary's `isDimensionUnit`), at the
 * ratio of one the invoice sync seeds. Such a pack is counted once, in its own unit: the "PCS" the
 * sync writes beside it and the "1 per LBS" it implies are defaults (`buildUom` ->
 * `resolveCountUnitName`, D19), not a count of anything on the paper, so "88 LBS / 88 PCS" and a
 * "1 per LBS" line told staff a weight item was also 88 pieces (lane 5, V4). Only while the stored
 * ratio is the seed's 1: a real count per pack is a fact and is shown. The ONE condition, read by the
 * list's quantity and by the audit modal's "per pack" line.
 */
const isSeededMeasurePack = (uom?: PackUom | null) =>
  isDimensionUnit(String(uom?.level2Name || '').trim()) && (num(uom?.level1PerLevel2) ?? 1) === 1;

/** `3 CASE / 24 OZ`: both levels, each with whatever name the row carries. */
const formatBothLevels = (
  level2: number | null | undefined,
  level1: number | null | undefined,
  uom?: PackUom | null
) => {
  const l2Name = String(uom?.level2Name || '').trim();
  const l1Name = String(uom?.level1Name || '').trim();
  const l2 = num(level2);
  const l1 = num(level1);
  // Stock is held at level 1 (`getAvailableQty`), so for a seeded measure pack that figure carries the
  // pack's own unit.
  if (isSeededMeasurePack(uom)) {
    const held = l1 ?? l2;
    return held === null ? '--' : `${held} ${l2Name}`;
  }
  const parts: string[] = [];
  // A zero case count is still worth printing when the row is denominated in
  // cases: "0 CASE / 6 OZ" is a loose remainder, and that is the useful reading.
  // A MISSING one is left off entirely, because it is not a zero: printing
  // "0 CASE" for a case count nothing records claims the warehouse is empty of
  // something this row cannot speak for. Same for the level-1 figure.
  if (l2 !== null && (l2Name || l2 > 0)) parts.push(`${l2}${l2Name ? ` ${l2Name}` : ''}`);
  if (l1 !== null) parts.push(`${l1}${l1Name ? ` ${l1Name}` : ''}`);
  return parts.length ? parts.join(' / ') : '--';
};

/**
 * The packaging disagreements a row is carrying (TECH_DEBT #52).
 *
 * `InventoryItem.packagingConflicts` is written by the vendor-invoice sync when
 * an invoice states packaging the row already contradicts: under
 * `DECISIONS.md` D1 the ROW wins, and the losing claim is written down for a
 * person instead of being applied. This is the reader — a flag nothing displays
 * is the shape TECH_DEBT #140 is about, and the catalogue's answer to the same
 * rule (`BUSINESS_RULES#invoice-vs-catalogue-packaging`) has had a badge and a
 * "Needs review" filter since the day it shipped.
 *
 * Cast rather than typed: `client/src/lib/inventoryApi.ts` owns the row type and
 * is not this lane's file to widen. The page already reads `uom` off the same
 * cast.
 */
type PackagingConflict = {
  fact?: string;
  itemValue?: string;
  invoiceValue?: string;
  invoiceNumber?: string;
  lineNumber?: number | null;
  message?: string;
};

const PACK_FACT_LABELS: Record<string, string> = {
  packUnit: 'Pack unit',
  countUnit: 'Unit',
  packRatio: 'Units per pack',
  weightTier: 'Weight per unit',
};

const packagingConflictsOf = (item: any): PackagingConflict[] => {
  const rows = (item as any)?.packagingConflicts;
  return Array.isArray(rows) ? rows.filter(Boolean) : [];
};

/**
 * A movement's level-2 figure as text, or null when there is nothing to say.
 *
 * The unit name comes off the movement's own snapshot, then the item's UOM, and
 * is NEVER defaulted to "CASE": this screen exists because a level-1 name lifted
 * out of a size string ("OZ") made a count of 8 read as a weight, and inventing
 * the level-2 name is the same mistake one row up.
 *
 * Zero is not printed. `InventoryMovement.qty.level2Qty` defaults to 0 in the
 * schema, so every loose-units movement carries one and the ledger would grow a
 * bare "0" under every row. Any other parsed value is shown, whatever its sign:
 * `normalizeQty` refuses a negative today (direction lives in `type`), and this
 * does not quietly depend on that staying true.
 */
const movementLevel2 = (movement: any, fallbackName?: string | null) => {
  const qty = num(movement?.qty?.level2Qty);
  if (qty === null || qty === 0) return null;
  const name = String(movement?.snapshot?.uomLevel2 || fallbackName || '').trim();
  return name ? `${qty} ${name}` : String(qty);
};

/**
 * Whole cases a layer's remaining level-1 quantity comes to, or null when the
 * row cannot say. Display only, and deliberately null on a fractional result:
 * "1.5 CASE" invites someone to pick one and a half boxes, where the honest
 * answer is the level-1 figure the layer actually stores.
 */
const layerCaseCount = (layer: any) => {
  // `num`, not `Number(x || 0)`. A missing `qtyRemainingLevel1` under the old
  // spelling read as 0, divided cleanly, and rendered a confident "0 CASE" for a
  // quantity the row cannot speak for: the same "missing is not zero" mistake
  // `getStockLevels` above exists to avoid, and CLAUDE.md invariant 8.
  const per = num(layer?.level1PerLevel2);
  const remaining = num(layer?.qtyRemainingLevel1);
  if (per === null || remaining === null || !(per > 0)) return null;
  const cases = remaining / per;
  return Number.isInteger(cases) ? cases : null;
};

/**
 * What a layer holds for vendor returns (D44): "24 · DN-00003", or null when it
 * holds nothing. The figure is the server's `qtyHeldLevel1`, never re-summed here.
 */
const layerHeldLabel = (layer: StockLayerDto): string | null => {
  const held = layer.qtyHeldLevel1;
  if (typeof held !== 'number' || !(held > 0)) return null;
  const numbers = Array.from(
    new Set((layer.returnHolds || []).map((h) => h.debitNoteNumber).filter((n): n is string => Boolean(n)))
  );
  return numbers.length ? `${held} · ${numbers.join(', ')}` : String(held);
};

const getExpiryInfo = (it: InventoryItem) => {
  const expiryDate = (it as any)?.expiryDate ? new Date((it as any).expiryDate) : null;
  if (!expiryDate || Number.isNaN(expiryDate.getTime())) {
    return { isExpiring: false, isExpired: false, daysLeft: null };
  }
  // Today's local calendar day, as the same UTC-midnight form an expiry is stored in.
  const today = new Date();
  const startOfToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const diffMs = expiryDate.getTime() - startOfToday.getTime();
  const daysLeft = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const warningDays = Number((it as any)?.alerts?.expiryWarningDays || 7);
  return {
    isExpiring: daysLeft <= warningDays && daysLeft >= 0,
    isExpired: daysLeft < 0,
    daysLeft,
  };
};

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function parseCsv(text: string): string[][] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map(parseCsvLine);
}

function normalizeHeader(h: string) {
  return String(h || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// D95.5: pantry is on hold — only Yelo's sync creates pantry items. The server
// refuses it with this code; the CSV mapper refuses it before sending.
const PANTRY_REFUSED_TEXT = 'Pantry items come from Yelo; this row cannot be uploaded.';
export const pantryRefusedMessage = (e: any): string | undefined =>
  e?.code === 'INVENTORY_PANTRY_SOURCE_REFUSED' ? PANTRY_REFUSED_TEXT : undefined;

function normalizeSource(s: string) {
  const t = String(s || '').toLowerCase().trim();
  if (t === 'invoice') return 'invoice';
  if (t === 'pantry') return 'pantry';
  return 'manual';
}

// The Edit/Add Item modal's weight-per-piece field. InventoryItem.uom.weightUnit
// is enum ['kg','lb',null] (src/models/InventoryItem.js) — the same normalise-
// to-kg convention every server writer uses (itemGroup.service.weightTierFromSize)
// — so a reviewer typing "320" + "G" here has to land on the same kg value the
// vendor-invoice sync would have derived from "320 G" on the Unit Size cell.
const WEIGHT_UNIT_OPTIONS = ['G', 'KG', 'LB', 'OZ'] as const;
type WeightUnitOption = (typeof WEIGHT_UNIT_OPTIONS)[number];
const KG_PER_LB = KG_PER_UNIT.LBS;
const KG_PER_OZ = KG_PER_UNIT.OZ;

function weightAmountToKg(amount: number, unit: WeightUnitOption): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (unit === 'G') return amount / 1000;
  if (unit === 'LB') return amount * KG_PER_LB;
  if (unit === 'OZ') return amount * KG_PER_OZ;
  return amount; // KG
}

// Reverse, for populating the form from a stored kg value: grams under 1kg,
// kg at or above it, so it reads "320g", not "0.32kg".
function kgToWeightDisplay(kg: number | null | undefined): { amount: string; unit: WeightUnitOption } {
  if (!Number.isFinite(kg as number) || (kg as number) <= 0) return { amount: '', unit: 'G' };
  const k = kg as number;
  const trim = (n: number) => String(Number(n.toFixed(6)));
  if (k >= 1) return { amount: trim(k), unit: 'KG' };
  return { amount: trim(k * 1000), unit: 'G' };
}

/* ----------------------------------------------------------
   CSV bulk upload — row -> create payload (TECH_DEBT #142)
---------------------------------------------------------- */

// The columns this screen's CSV upload understands. `name` is the only required
// one; every other column may be absent, and absent has to keep meaning absent
// (invariant 8) rather than collapsing to a zero or a guess.
//
// `units per case` / `weight per unit` / `weight unit` are new (TECH_DEBT #142).
// They are OPTIONAL on purpose: files people already have do not carry them, and
// a new required column would reject every one of those files — a worse defect
// than the one this fixes.
const CSV_COLUMN_ALIASES = {
  name: ['name'],
  category: ['category'],
  subCategory: ['sub category', 'subcategory'],
  packaging: ['packaging'],
  unit: ['unit'],
  priceTT: ['price tt', 'pricett'],
  expiryDate: ['expiry date'],
  source: ['source'],
  unitsPerCase: ['units per case', 'unitspercase', 'units/case'],
  weightPerUnit: ['weight per unit', 'weightperunit'],
  weightUnit: ['weight unit', 'weightunit'],
} as const;

/**
 * The pack/piece NAMES for a CSV row.
 *
 * This replaces `deriveUom`, whose pack branch required a LEADING DIGIT before
 * it would read a name at all (`/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/`), so a plain
 * "CASE" fell through to the literal default "Pack" and a case-packed item
 * mislabelled its own pack unit on creation (TECH_DEBT #142).
 *
 * Order of readings, and why each one is here:
 *
 *  1. `canonicalUnit` — the vocabulary's own answer, and the same first reading
 *     `vendorInvoiceInventorySync.buildUom` makes for this very field
 *     (`canonicalUnit(level2Raw) || level2Raw`). "CASE" -> "CASE", "cases" ->
 *     "CASE", "Pack" -> "PACK". Every no-digit value the old regex got wrong is
 *     answered here, by the owner, with no new guesser.
 *  2. the old word reading, digit now OPTIONAL — kept ONLY for backwards
 *     compatibility with CSVs already in use, where `packaging` is free text
 *     like "12 PACK". Taking that verbatim (what the Edit Item modal does with
 *     its own labelled field) would store `level2Name: "12 PACK"`, and
 *     `procurementProposal.resolveOrderLevel` decides which UoM TIER an order
 *     line draws from by prefix-matching its free-text unit against this exact
 *     string — "PACK" no longer matches "12 pack", so the proposal would
 *     silently drop from level2 to level1. That is a quantity change on rows a
 *     person only re-imported, so this reading does not move.
 *  3. the default. "PACK"/"PCS", the spellings the Add/Edit Item modal uses and
 *     the ones `unitVocabulary` recognises — the old defaults were "Pack" and
 *     "Item", and "Item" is not a unit in any vocabulary here.
 */
const CSV_NAME_WORD_RE = /(\d+(?:\.\d+)?)?\s*([a-zA-Z]+)/;

function csvUomName(raw: string, fallback: string): string {
  const text = String(raw || '').trim();
  if (!text) return fallback;
  const canonical = canonicalUnit(text);
  if (canonical) return canonical;
  const word = text.match(CSV_NAME_WORD_RE);
  if (word && word[2]) return word[2];
  return fallback;
}

export type InventoryCsvRowIssue = { row: number; message: string };

/**
 * One CSV row -> the `uom` block the create payload carries.
 *
 * `deriveUom` hard-coded `level1PerLevel2: 1` and never looked at a size at all,
 * so a bulk-imported item could not carry "14 units of 320 G each" the way an
 * invoice-synced or hand-edited one can. Both now come from their own columns.
 *
 * ABSENT `units per case` keeps today's answer, 1 — `InventoryItem.uom.
 * level1PerLevel2` is `required: true, min: 1`, so there is no "unknown" this
 * field can hold, and 1 is what every row created through this path already
 * got. It is not a claim that the row STATED 1; the upload modal says so.
 *
 * ABSENT weight stays null / null, not 0 — a 0 kg piece is a statement, and a
 * weight tier feeds `costEngine.computeCostLevels`' per-kg and per-lb prices.
 * A weight amount with no `weight unit` is REFUSED rather than assumed to be
 * grams: 1.5 read as grams instead of kilograms is a 1000x error in a money
 * column, and this column is new, so refusing breaks no existing file.
 */
function csvUom(
  fields: { packaging: string; unit: string; unitsPerCase: string; weightPerUnit: string; weightUnit: string },
  rowNumber: number,
  issues: InventoryCsvRowIssue[]
) {
  const level2Name = csvUomName(fields.packaging, 'PACK');
  const level1Name = csvUomName(fields.unit, 'PCS');

  let level1PerLevel2 = 1;
  const unitsPerCaseText = String(fields.unitsPerCase || '').trim();
  if (unitsPerCaseText) {
    const parsed = num(unitsPerCaseText);
    if (parsed == null || !(parsed > 0)) {
      issues.push({ row: rowNumber, message: `Row ${rowNumber}: "units per case" must be a positive number.` });
    } else {
      level1PerLevel2 = parsed;
    }
  }

  let weightPerLevel1: number | null = null;
  const weightText = String(fields.weightPerUnit || '').trim();
  const weightUnitText = String(fields.weightUnit || '').trim().toUpperCase();
  const knownWeightUnit = (WEIGHT_UNIT_OPTIONS as readonly string[]).includes(weightUnitText);
  if (weightText) {
    const parsedWeight = num(weightText);
    if (parsedWeight == null || !(parsedWeight > 0)) {
      issues.push({ row: rowNumber, message: `Row ${rowNumber}: "weight per unit" must be a positive number.` });
    } else if (!knownWeightUnit) {
      issues.push({
        row: rowNumber,
        message: `Row ${rowNumber}: "weight per unit" needs a "weight unit" column of ${WEIGHT_UNIT_OPTIONS.join('/')}.`,
      });
    } else {
      weightPerLevel1 = weightAmountToKg(parsedWeight, weightUnitText as WeightUnitOption);
    }
  } else if (weightUnitText && !knownWeightUnit) {
    issues.push({
      row: rowNumber,
      message: `Row ${rowNumber}: "weight unit" must be one of ${WEIGHT_UNIT_OPTIONS.join('/')}.`,
    });
  }

  return {
    level2Name,
    level1Name,
    level1PerLevel2,
    // Normalised to kg, the one convention `InventoryItem.uom.weightUnit` enums
    // to and every server writer uses (itemGroup.weightTierFromSize).
    weightUnit: weightPerLevel1 != null ? ('kg' as const) : null,
    weightPerLevel1,
  };
}

/**
 * The whole CSV -> the create payloads the upload posts, plus what is wrong with
 * it. Exported so the mapping this screen actually performs can be tested
 * without driving a file picker; `handleUpload` is the only caller.
 */
export function mapInventoryCsvRows(rows: string[][]): {
  payloads: CreateInventoryItemData[];
  missingColumns: string[];
  issues: InventoryCsvRowIssue[];
} {
  const headers = (rows[0] || []).map(normalizeHeader);
  const missingColumns = CSV_COLUMN_ALIASES.name.filter((h) => !headers.includes(h));
  if (missingColumns.length) return { payloads: [], missingColumns, issues: [] };

  const getVal = (row: string[], key: keyof typeof CSV_COLUMN_ALIASES) => {
    for (const alias of CSV_COLUMN_ALIASES[key] as readonly string[]) {
      const idx = headers.indexOf(alias);
      if (idx >= 0 && String(row[idx] || '').trim()) return String(row[idx]).trim();
    }
    return '';
  };

  const issues: InventoryCsvRowIssue[] = [];
  const payloads: CreateInventoryItemData[] = [];

  rows.slice(1).forEach((row, i) => {
    const name = getVal(row, 'name');
    if (!name) return;
    // 1-based and counting the header, so the number matches what a spreadsheet
    // shows the person who has to fix the row.
    const rowNumber = i + 2;

    const packaging = getVal(row, 'packaging');
    const unit = getVal(row, 'unit');
    const uom = csvUom(
      {
        packaging,
        unit,
        unitsPerCase: getVal(row, 'unitsPerCase'),
        weightPerUnit: getVal(row, 'weightPerUnit'),
        weightUnit: getVal(row, 'weightUnit'),
      },
      rowNumber,
      issues
    );

    // USC-BUG-0041: `new Date('2027-02-31')` is 3 March, and `new Date('31-02-2027')`
    // is Invalid Date whose `.toISOString()` threw and killed the whole import.
    // The owner reads day-first and refuses an impossible calendar date; an
    // unreadable cell is a row issue, so nothing is uploaded (same rule as a bad pack cell).
    const expiryDateRaw = getVal(row, 'expiryDate');
    const expiry = expiryDateRaw ? toInvoiceDate(expiryDateRaw) : null;
    if (expiryDateRaw && !expiry) {
      issues.push({
        row: rowNumber,
        message: `Row ${rowNumber}: "expiry date" "${expiryDateRaw}" is not a real date.`,
      });
    }

    const source = normalizeSource(getVal(row, 'source'));
    if (source === 'pantry') issues.push({ row: rowNumber, message: `Row ${rowNumber}: ${PANTRY_REFUSED_TEXT}` });

    payloads.push({
      name,
      category: getVal(row, 'category'),
      subCategory: getVal(row, 'subCategory'),
      packaging,
      unit,
      priceTT: num(getVal(row, 'priceTT')) ?? 0,
      expiryDate: expiry ? expiry.toISOString() : undefined,
      source,
      uom,
      // No `stock`: an item opens at zero and balances move only through
      // InventoryMovement apply/reverse (inventory.service.js); createItem refuses the field.
    } as CreateInventoryItemData);
  });

  return { payloads, missingColumns: [], issues };
}

/* ----------------------------------------------------------
   Component
---------------------------------------------------------- */
export const InventoryPage: React.FC<{ division?: InventoryDivision }> = ({ division = 'ship' }) => {
  const navigate = useNavigate();
  const isPantry = division === 'pantry';
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [auditItem, setAuditItem] = useState<InventoryItem | null>(null);
  const [auditMovements, setAuditMovements] = useState<any[]>([]);
  // Which individual receipts make up this item's balance, and whether they
  // still add up to it.
  const [stockLayers, setStockLayers] = useState<{
    united: StockLayerAvailability;
    pantry: StockLayerAvailability;
    reconciliation: StockLayerReconciliation | null;
  } | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  // D1-D5: per-vessel allocation breakdown for the open item
  const [vesselBreakdown, setVesselBreakdown] = useState<{
    totalAllocated: number;
    perVesselArray: Array<{ vessel: string; qty: number }>;
  }>({ totalAllocated: 0, perVesselArray: [] });
  // D2-D3: case-break split form (allocate qty to N vessels at once)
  //
  // `caseRefs` is set only for catch-weight goods, where a row ships named
  // boxes rather than a quantity: 50 kg of ribeye is cases 2, 3, 7 and 9
  // weighing 50.10, and the weight follows from the boxes rather than the
  // other way round.
  //
  // The pick suggested against a row's quantity (`plan`) lives ON the row, and
  // `id` is its React key and the handle async work uses to find it again. Both
  // used to be keyed by array position, so removing a row slid the next one into
  // its slot with the removed row's boxes (D117.1).
  const [splitRows, setSplitRows] = useState<SplitRow[]>(() => [newSplitRow()]);
  // The individual boxes on the shelf for the open item, and the id of the row
  // whose suggestion is being fetched.
  const [itemCases, setItemCases] = useState<CasePlanResponse | null>(null);
  const [casePlanBusy, setCasePlanBusy] = useState<number | null>(null);
  const [splitFrom, setSplitFrom] = useState<'united' | 'pantry'>('united');
  const [splitBusy, setSplitBusy] = useState(false);
  // D3 — real case-break (open sealed cases at a location, produce loose units)
  const [caseBreakLoc, setCaseBreakLoc] = useState<'united' | 'pantry'>('united');
  const [caseBreakCount, setCaseBreakCount] = useState<string>('');
  const [caseBreakReason, setCaseBreakReason] = useState<string>('');
  const [caseBreakBusy, setCaseBreakBusy] = useState<boolean>(false);

  // E3 — Pantry ↔ USC transfer form state
  const [transferDirection, setTransferDirection] = useState<'united_to_pantry' | 'pantry_to_united'>('united_to_pantry');
  const [transferQty, setTransferQty] = useState<string>('');
  const [transferNote, setTransferNote] = useState<string>('');
  const [transferBusy, setTransferBusy] = useState(false);

  // D4 — Pull retained units between vessels
  const [pullFromVessel, setPullFromVessel] = useState<string>('');
  const [pullToVessel, setPullToVessel] = useState<string>('');
  const [pullQty, setPullQty] = useState<string>('');
  const [pullReason, setPullReason] = useState<string>('');
  const [pullBusy, setPullBusy] = useState(false);
  const [packagingFilter, setPackagingFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [minQtyFilter, setMinQtyFilter] = useState('');
  const [minPriceFilter, setMinPriceFilter] = useState('');

  const [sourceFilter, setSourceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [lowStockFilter, setLowStockFilter] = useState('all');

  // Add modal
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  // Upload modal
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  // A PDF from here is a vendor invoice, and the server refuses one with no answer
  // to "was it already paid?" (D49). No default: a person picks.
  const [uploadPayment, setUploadPayment] = useState<PaymentDraft>(EMPTY_PAYMENT_DRAFT);
  const [uploadPaidDateValid, setUploadPaidDateValid] = useState(true);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  // Delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Form state
  const [fItemName, setFItemName] = useState('');
  const [fExpiry, setFExpiry] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fSubCategory, setFSubCategory] = useState('');
  const [fPackaging, setFPackaging] = useState('');
  const [fUnit, setFUnit] = useState('');
  // "14 units of 320 G each" — the pack ratio and the per-piece weight tier,
  // both stored on InventoryItem.uom, both invisible on this form until now.
  const [fUnitsPerCase, setFUnitsPerCase] = useState('');
  const [fWeightAmount, setFWeightAmount] = useState('');
  const [fWeightUnit, setFWeightUnit] = useState<WeightUnitOption>('G');
  const [fTT, setFTT] = useState('');
  // USD removed (TT only)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // D109.6: false while the modal's expiry box holds a refused date (`fExpiry` is then the OLD one).
  const [fExpiryValid, setFExpiryValid] = useState(true);
  const normalizeDecimalInput = (value: string) => {
    let out = value.replace(/[^0-9.]/g, '');
    const firstDot = out.indexOf('.');
    if (firstDot >= 0) {
      out = out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, '');
    }
    return out;
  };

  const setFieldError = (key: string, message?: string) => {
    setFormErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  };

  const load = async (opts?: { page?: number }) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await getInventoryItems({
        q: query.trim() || undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        division,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        subCategory: subCategoryFilter !== 'all' ? subCategoryFilter : undefined,
        expiryFilter: expiryFilter !== 'all' ? expiryFilter : undefined,
        lowStockOnly: lowStockFilter === 'low',
        page: opts?.page || page,
        limit,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
      setPage(res.page || (opts?.page || page));
    } catch (e: any) {
      setErr(e?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ page: 1 });
  }, [division, query, sourceFilter, categoryFilter, subCategoryFilter, expiryFilter, lowStockFilter]);

  useEffect(() => {
    if (!actionMsg) return;
    const t = setTimeout(() => {
      setActionMsg(null);
    }, 2500);
    return () => clearTimeout(t);
  }, [actionMsg]);

  // USC-BUG-0042 — the options come from the whole (source-filtered) list on the
  // server, not from the 50 rows on screen, which left Category empty whenever
  // page 1 carried no category. Re-read when `items` reloads, so a category just
  // typed on an item appears without a page refresh.
  const [categories, setCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    getInventoryFacets({
      source: sourceFilter !== 'all' ? sourceFilter : undefined,
      division,
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
    })
      .then((facets) => {
        if (cancelled) return;
        setCategories(facets.categories);
        setSubCategories(facets.subCategories);
      })
      .catch((e: any) => {
        if (!cancelled) setActionMsg({ type: 'error', text: e?.message || 'Failed to load category options.' });
      });
    return () => {
      cancelled = true;
    };
  }, [division, sourceFilter, categoryFilter, items]);

  const filtered = useMemo(() => {
    const nameQ = query.trim().toLowerCase();
    const pkgQ = packagingFilter.trim().toLowerCase();
    const unitQ = unitFilter.trim().toLowerCase();
    const minQty = Number(minQtyFilter);
    const minPrice = Number(minPriceFilter);

    return items.filter((it) => {
      if (nameQ && !String(it.name || '').toLowerCase().includes(nameQ)) return false;
      if (pkgQ && !String(it.packaging || '').toLowerCase().includes(pkgQ)) return false;
      if (unitQ && !String(it.unit || '').toLowerCase().includes(unitQ)) return false;
      if (Number.isFinite(minQty) && getAvailableQty(it) < minQty) return false;
      const priceTT = Number((it as any)?.pricing?.priceTT ?? (it as any)?.priceTT ?? 0);
      if (Number.isFinite(minPrice) && priceTT < minPrice) return false;
      return true;
    });
  }, [items, query, packagingFilter, unitFilter, minQtyFilter, minPriceFilter]);

  const openAdd = () => {
    setEditingItem(null);
    setFormErrors({});
    setIsAddOpen(true);
  };
  const openEdit = (item: InventoryItem) => {
    if (String(item.source || '').toLowerCase() === 'pantry') {
      setActionMsg({ type: 'error', text: 'Pantry items are read-only. Edit invoice/manual items only.' });
      return;
    }

    setEditingItem(item);
    setFItemName(item.name || '');
    setFExpiry(inputDateValue((item as any)?.expiryDate || null));
    setFCategory(item.category || '');
    setFSubCategory(item.subCategory || '');
    setFPackaging(item.packaging || '');
    setFUnit(item.unit || '');
    setFUnitsPerCase(
      typeof item.uom?.level1PerLevel2 === 'number' && item.uom.level1PerLevel2 > 1
        ? String(item.uom.level1PerLevel2)
        : ''
    );
    const weightDisplay = kgToWeightDisplay(
      item.uom?.weightUnit === 'lb'
        ? (item.uom?.weightPerLevel1 || 0) * KG_PER_LB
        : item.uom?.weightPerLevel1
    );
    setFWeightAmount(weightDisplay.amount);
    setFWeightUnit(weightDisplay.unit);
    setFTT(String((item as any)?.pricing?.priceTT ?? (item as any)?.priceTT ?? ''));
    setFormErrors({});
    setIsAddOpen(true);
  };
  const closeAdd = () => {
    setIsAddOpen(false);
    setEditingItem(null);
    setFItemName('');
    setFExpiry('');
    setFCategory('');
    setFSubCategory('');
    setFPackaging('');
    setFUnit('');
    setFUnitsPerCase('');
    setFWeightAmount('');
    setFWeightUnit('G');
    setFTT('');
    setFormErrors({});
  };

  const addItem = async () => {
    // A second click while the first POST is in flight used to create a twin
    // (USC-BUG-0038); the button is disabled too, this covers the same tick.
    if (savingItem) return;
    const nextErrors: Record<string, string> = {};
    if (!fItemName.trim()) nextErrors.itemName = 'Item name is required.';
    // The owner, not `new Date`: where the browser falls back to a text box,
    // "2027-02-31" would otherwise roll into March and save (USC-BUG-0041).
    const expiry = fExpiry ? toInvoiceDate(fExpiry) : null;
    if (fExpiry && !expiry) nextErrors.expiryDate = 'Invalid date.';
    if (!fExpiryValid) nextErrors.expiryDate = 'Fix the expiry date first: it is not a real day.';
    if (fTT && Number.isNaN(Number(fTT))) nextErrors.priceTT = 'Must be a number.';
    if (fUnitsPerCase.trim() && !(Number(fUnitsPerCase) > 0)) {
      nextErrors.unitsPerCase = 'Must be a positive number.';
    }
    if (fWeightAmount.trim() && !(Number(fWeightAmount) > 0)) {
      nextErrors.weightAmount = 'Must be a positive number.';
    }
    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      setActionMsg({ type: 'error', text: 'Please fix the highlighted fields.' });
      return;
    }
    setFormErrors({});

    // Built directly from the explicit fields below rather than guessed with
    // `deriveUom` out of the Packaging/Unit free text — that regex requires a
    // leading digit ("2 pack") and silently fell back to level2Name "Pack" for
    // plain codes like "CASE", which reset uom.level2Name on every edit of an
    // invoice-synced item. `level1PerLevel2` / the weight tier fall back to
    // whatever the item already had (not to 1 / none) so leaving these fields
    // untouched on an existing item cannot erase data an invoice sync wrote.
    const unitsPerCaseNum = fUnitsPerCase.trim() ? Number(fUnitsPerCase) : null;
    const level1PerLevel2 =
      unitsPerCaseNum != null && unitsPerCaseNum > 0
        ? unitsPerCaseNum
        : editingItem?.uom?.level1PerLevel2 || 1;
    const weightAmountNum = fWeightAmount.trim() ? Number(fWeightAmount) : null;
    const weightKg = weightAmountNum != null ? weightAmountToKg(weightAmountNum, fWeightUnit) : null;
    const uom = {
      level2Name: fPackaging.trim() || editingItem?.uom?.level2Name || 'PACK',
      level1Name: fUnit.trim() || editingItem?.uom?.level1Name || 'PCS',
      level1PerLevel2,
      weightUnit: weightKg != null ? ('kg' as const) : null,
      weightPerLevel1: weightKg,
    };

    const payload: CreateInventoryItemData = {
      name: fItemName.trim(),
      category: fCategory.trim(),
      subCategory: fSubCategory.trim(),
      packaging: fPackaging.trim(),
      unit: fUnit.trim(),
      priceTT: Number(fTT) || 0,
      priceUSD: 0,
      expiryDate: expiry ? expiry.toISOString() : undefined,
      source: 'manual',
      uom,
    };

    setSavingItem(true);
    try {
      if (editingItem?._id || editingItem?.id) {
        const itemId = String(editingItem._id || editingItem.id);
        await updateInventoryItem(itemId, {
          name: payload.name,
          category: payload.category,
          subCategory: payload.subCategory,
          packaging: payload.packaging,
          unit: payload.unit,
          source: String(editingItem.source || 'manual') as any,
          expiryDate: payload.expiryDate,
          uom,
          pricing: {
            priceTT: payload.priceTT || 0,
            priceUSD: 0,
          },
        });
      } else {
        await createInventoryItem(payload);
      }
      await load({ page });
      closeAdd();
      setActionMsg({
        type: 'success',
        text: editingItem ? 'Item updated successfully.' : 'Item added successfully.',
      });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || (editingItem ? 'Failed to update item.' : 'Failed to add item.') });
    } finally {
      setSavingItem(false);
    }
  };

  const requestDelete = (id: string) => setConfirmDeleteId(id);
  const cancelDelete = () => setConfirmDeleteId(null);

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteInventoryItem(confirmDeleteId);
    } catch (err: any) {
      // The dialog stays open with the item still listed — nothing happened.
      window.alert(err?.message || 'The item could not be deleted.');
      return;
    }
    setConfirmDeleteId(null);
    await load({ page });
  };

  const handleEdit = (item: InventoryItem) => {
    openEdit(item);
  };

  const openAuditTrail = async (item: InventoryItem) => {
    setAuditItem(item);
    setAuditMovements([]);
    setVesselBreakdown({ totalAllocated: 0, perVesselArray: [] });
    setStockLayers(null);
    setItemCases(null);
    // A reload (after a transfer, a pull, a case break) means the shelf may have
    // changed: suggestions and the boxes they named are no longer to be trusted.
    setSplitRows((rows) => rows.map(withoutCases));
    setAuditLoading(true);
    try {
      const itemId = String(item._id || item.id);
      const [movements, breakdown, layers, cases] = await Promise.all([
        getInventoryMovements({ itemId }),
        getVesselAllocationBreakdown(itemId),
        getItemStockLayers(itemId),
        getItemCasePlan(itemId),
      ]);
      setAuditMovements(Array.isArray(movements) ? movements : []);
      setVesselBreakdown({
        totalAllocated: breakdown?.totalAllocated || 0,
        perVesselArray: Array.isArray(breakdown?.perVesselArray) ? breakdown.perVesselArray : [],
      });
      setStockLayers(layers);
      setItemCases(cases);
    } catch (e: any) {
      setAuditMovements([]);
      setActionMsg({ type: 'error', text: e?.message || 'Failed to load audit trail.' });
    } finally {
      setAuditLoading(false);
    }
  };

  const closeAuditTrail = () => {
    setAuditItem(null);
    setAuditMovements([]);
    setVesselBreakdown({ totalAllocated: 0, perVesselArray: [] });
    setAuditLoading(false);
    setSplitRows([newSplitRow()]);
    setCasePlanBusy(null);
    setSplitFrom('united');
    setTransferDirection('united_to_pantry');
    setTransferQty('');
    setTransferNote('');
    setPullFromVessel('');
    setPullToVessel('');
    setPullQty('');
    setPullReason('');
  };

  // D4 — Pull retained units between vessels
  const submitPullRetained = async () => {
    if (!auditItem || pullBusy) return;
    const itemId = String(auditItem._id || auditItem.id || '');
    const qty = Number(pullQty);
    if (!pullFromVessel.trim() || !pullToVessel.trim()) {
      setActionMsg({ type: 'error', text: 'Both from and to vessel names are required.' });
      return;
    }
    if (pullFromVessel === pullToVessel) {
      setActionMsg({ type: 'error', text: 'From and to vessels must differ.' });
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      setActionMsg({ type: 'error', text: 'Enter a pull quantity > 0.' });
      return;
    }
    setPullBusy(true);
    setActionMsg(null);
    try {
      await pullRetained(itemId, {
        fromVesselName: pullFromVessel.trim(),
        toVesselName: pullToVessel.trim(),
        level1Qty: qty,
        reason: pullReason,
      });
      setActionMsg({ type: 'success', text: `Pulled ${qty} units: ${pullFromVessel} → ${pullToVessel}.` });
      setPullFromVessel('');
      setPullToVessel('');
      setPullQty('');
      setPullReason('');
      await openAuditTrail(auditItem);
      await load({ page });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || 'Pull failed.' });
    } finally {
      setPullBusy(false);
    }
  };

  // E3 — Pantry ↔ USC transfer submit. Creates a TRANSFER movement (approved
  // immediately) that debits one side and credits the other.
  const submitPantryTransfer = async () => {
    if (!auditItem || transferBusy) return;
    const itemId = String(auditItem._id || auditItem.id || '');
    const qty = Number(transferQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      setActionMsg({ type: 'error', text: 'Enter a transfer quantity > 0.' });
      return;
    }
    const fromLoc = transferDirection === 'united_to_pantry' ? 'united' : 'pantry';
    const toLoc = transferDirection === 'united_to_pantry' ? 'pantry' : 'united';
    const sourceStock = fromLoc === 'united'
      ? Number((auditItem as any)?.stock?.united?.level1Qty || 0)
      : Number((auditItem as any)?.stock?.pantry?.level1Qty || 0);
    if (qty > sourceStock) {
      setActionMsg({ type: 'error', text: `Transfer qty ${qty} exceeds ${fromLoc} stock (${sourceStock}).` });
      return;
    }
    setTransferBusy(true);
    setActionMsg(null);
    try {
      // One request, one server transaction (D113.3): the stock moves or nothing
      // is written. A refusal reaches the outer catch and is shown — never a
      // success line for a transfer that did not happen (CodeRabbit, 2026-08-21).
      await createAndApplyInventoryMovement({
        itemId,
        type: 'transfer',
        qty: { level1Qty: qty, level2Qty: 0 },
        from: { location: fromLoc },
        to: { location: toLoc },
        requireApproval: false,
        reason: 'pantry_transfer',
        note: transferNote ? `Transfer ${fromLoc}→${toLoc}: ${transferNote}` : `Transfer ${fromLoc}→${toLoc}`,
      });
      setActionMsg({ type: 'success', text: `Transferred ${qty} units ${fromLoc} → ${toLoc}.` });
      setTransferQty('');
      setTransferNote('');
      await openAuditTrail(auditItem);
      await load({ page });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || 'Transfer failed.' });
    } finally {
      setTransferBusy(false);
    }
  };

  // Catch weight: ask the server which boxes make this row's quantity.
  //
  // The answer never goes under — 50 kg of ribeye comes back as four cases
  // weighing 50.10 — because 100 g over is a rounding difference on an invoice
  // and going under is a shortfall the vessel discovers at sea.
  const suggestCases = async (rowId: number) => {
    if (!auditItem) return;
    const itemId = String(auditItem._id || auditItem.id || '');
    const target = Number(splitRows.find((r) => r.id === rowId)?.qty);
    if (!itemId || !Number.isFinite(target) || target <= 0) {
      setActionMsg({ type: 'error', text: 'Enter how much this vessel needs first.' });
      return;
    }

    setCasePlanBusy(rowId);
    try {
      const res = await getItemCasePlan(itemId, { location: splitFrom, target });
      setItemCases(res);
      // By id: the row may have moved up the list, or been removed, while the
      // server worked, and then the answer has nowhere to land.
      setSplitRows((rows) => rows.map((r) => (r.id === rowId ? { ...r, plan: res.plan } : r)));
      if (!res.plan) {
        setActionMsg({
          type: 'error',
          text: res.note || 'This item is not picked by case — allocate by quantity instead.',
        });
      }
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || 'Could not work out a case pick.' });
    } finally {
      setCasePlanBusy(null);
    }
  };

  /** Add or remove one box from a suggestion — a picker finding one damaged. */
  const toggleCaseInPlan = (rowId: number, target: StockCaseDto) => {
    setSplitRows((rows) => rows.map((row) => {
      const plan = row.plan;
      if (row.id !== rowId || !plan) return row;
      const isPicked = plan.picks.some(
        (c) => c.layerId === target.layerId && c.seq === target.seq
      );
      const picks = isPicked
        ? plan.picks.filter((c) => !(c.layerId === target.layerId && c.seq === target.seq))
        : [...plan.picks, target].sort((a, b) => a.seq - b.seq);
      const total = Math.round(picks.reduce((s, c) => s + c.weightRemainingLevel1, 0) * 1000) / 1000;
      return {
        ...row,
        plan: {
          ...plan,
          picks,
          total,
          count: picks.length,
          satisfied: total >= plan.targetLevel1,
          overage: total >= plan.targetLevel1 ? Math.round((total - plan.targetLevel1) * 1000) / 1000 : 0,
          shortfall: total >= plan.targetLevel1 ? 0 : Math.round((plan.targetLevel1 - total) * 1000) / 1000,
          exact: total === plan.targetLevel1,
        },
      };
    }));
  };

  /** Commit a suggestion onto its row: the boxes, and the weight they come to. */
  const acceptCasePlan = (rowId: number) => {
    setSplitRows((rows) => rows.map((row) => {
      const plan = row.plan;
      if (row.id !== rowId || !plan || !plan.picks.length) return row;
      return {
        ...row,
        qty: String(plan.total),
        caseRefs: plan.picks.map((c) => ({ layerId: c.layerId, seq: c.seq })),
      };
    }));
  };

  /** Drop the boxes off a row and go back to allocating a plain quantity. */
  const clearCasePlan = (rowId: number) => {
    setSplitRows((rows) => rows.map((row) => (row.id === rowId ? withoutCases(row) : row)));
  };

  // D2-D3: submit a case-break split — one ALLOCATE_TO_SHIP movement per vessel,
  // auto-approved so the warehouse can act immediately.
  // D113.5 (owner): all or nothing. Every row goes in ONE request and one server
  // transaction; a refused row (short stock, a case another picker took) moves
  // nothing for any vessel, and the message names that row.
  const submitVesselSplit = async () => {
    if (!auditItem || splitBusy) return;
    const itemId = String(auditItem._id || auditItem.id || '');
    if (!itemId) return;

    const entered = splitRows.map((r, i) => ({
      screenRow: i + 1,
      vessel: String(r.vessel || '').trim(),
      qtyText: String(r.qty ?? '').trim(),
      qty: Number(r.qty),
      caseRefs: r.caseRefs,
    }));
    // D113.6 — only a fully blank row is skipped. A half-filled one used to be
    // dropped without a word, so the rest of the split went out without the
    // vessel the user had started on; it is refused by row instead.
    const rows = entered.filter((r) => r.vessel || r.qtyText || r.caseRefs?.length);
    const half = rows.find((r) => !r.vessel || !(r.caseRefs?.length || (Number.isFinite(r.qty) && r.qty > 0)));
    if (half) {
      const missing = half.vessel ? 'a quantity above 0' : 'a vessel name';
      setActionMsg({ type: 'error', text: `Row ${half.screenRow} needs ${missing}. Nothing was allocated.` });
      return;
    }

    if (rows.length === 0) {
      setActionMsg({ type: 'error', text: 'Enter at least one vessel + qty to allocate.' });
      return;
    }
    const totalToAllocate = rows.reduce((s, r) => s + r.qty, 0);
    const sourceStock = splitFrom === 'united'
      ? Number((auditItem as any)?.stock?.united?.level1Qty || 0)
      : Number((auditItem as any)?.stock?.pantry?.level1Qty || 0);
    if (totalToAllocate > sourceStock) {
      setActionMsg({
        type: 'error',
        text: `Total split qty ${totalToAllocate} exceeds ${splitFrom} stock (${sourceStock}).`,
      });
      return;
    }

    setSplitBusy(true);
    setActionMsg(null);
    try {
      await allocateSplitToVessels(itemId, {
        location: splitFrom,
        rows: rows.map((row): VesselSplitRow =>
          // Catch weight: this row ships named boxes, so the server derives the
          // weight from them and there is no quantity for the two to disagree on.
          row.caseRefs?.length
            ? {
                vesselName: row.vessel,
                caseRefs: row.caseRefs,
                note: `Case pick: ${row.caseRefs.length} case(s) → ${row.vessel}`,
              }
            : { vesselName: row.vessel, qty: row.qty, note: `Case-break split: ${row.qty} → ${row.vessel}` }
        ),
      });
      setActionMsg({
        type: 'success',
        text: `Allocated ${totalToAllocate} units across ${rows.length} vessel(s).`,
      });
      setSplitRows([newSplitRow()]);
      // refresh breakdown + movements
      await openAuditTrail(auditItem);
      await load({ page });
    } catch (e: any) {
      // The server counts the rows it was sent (1-based); blank rows were not
      // sent, so its row is mapped back to the row on screen. The facts come
      // from `details`, never from the message.
      const sentRow = Number(e?.details?.row);
      const failed = Number.isInteger(sentRow) && sentRow >= 1 ? rows[sentRow - 1] : undefined;
      const where = failed ? `Row ${failed.screenRow} (${String(e?.details?.vesselName || failed.vessel)})` : '';
      const serverFault = !e?.status || e.status >= 500;
      let text: string;
      if (serverFault) {
        text = where
          ? `Nothing was allocated. ${where} could not be saved because of a server error. Try again.`
          : 'Nothing was allocated. The split could not be saved because of a server error. Try again.';
      } else {
        const reason = e?.message || 'The split was refused.';
        text = where ? `Nothing was allocated. ${where} was refused: ${reason}` : `Nothing was allocated. ${reason}`;
      }
      setActionMsg({ type: 'error', text });
    } finally {
      setSplitBusy(false);
    }
  };

  // D3 — Real case-break call (uses new POST /items/:id/break-case route).
  const submitCaseBreak = async () => {
    if (!auditItem || caseBreakBusy) return;
    const itemId = String(auditItem._id || auditItem.id || '');
    const cases = Number(caseBreakCount);
    if (!itemId || !Number.isFinite(cases) || cases <= 0) {
      setActionMsg({ type: 'error', text: 'Enter a positive case count to break.' });
      return;
    }
    const availableCases = caseBreakLoc === 'united'
      ? Number((auditItem as any)?.stock?.united?.level2Qty || 0)
      : Number((auditItem as any)?.stock?.pantry?.level2Qty || 0);
    if (cases > availableCases) {
      setActionMsg({ type: 'error', text: `Not enough sealed cases at ${caseBreakLoc}: have ${availableCases}, requested ${cases}.` });
      return;
    }
    setCaseBreakBusy(true);
    setActionMsg(null);
    try {
      const result = await breakCase(itemId, { location: caseBreakLoc, cases, reason: caseBreakReason });
      const produced = result?.looseUnitsProduced ?? 0;
      setActionMsg({ type: 'success', text: `Broke ${cases} case(s) at ${caseBreakLoc} → ${produced} loose unit(s).` });
      setCaseBreakCount('');
      setCaseBreakReason('');
      await openAuditTrail(auditItem);
      await load({ page });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || 'Failed to break cases.' });
    } finally {
      setCaseBreakBusy(false);
    }
  };

  const updateExpiry = async (itemId: string, value: string) => {
    const expiryDate = value ? new Date(value).toISOString() : null;
    try {
      await updateInventoryItem(itemId, { expiryDate: expiryDate || undefined } as any);
      await load({ page });
      setActionMsg({ type: 'success', text: 'Expiry date updated.' });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || 'Failed to update expiry date.' });
    }
  };

  const handleExport = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    setActionMsg(null);

    try {
      const all: InventoryItem[] = [];
      let exportPage = 1;
      let totalItems = 0;
      const exportLimit = 500;

      while (true) {
        const res = await getInventoryItems({
          q: query.trim() || undefined,
          source: sourceFilter !== 'all' ? sourceFilter : undefined,
          division,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          subCategory: subCategoryFilter !== 'all' ? subCategoryFilter : undefined,
          expiryFilter: expiryFilter !== 'all' ? expiryFilter : undefined,
          lowStockOnly: lowStockFilter === 'low',
          page: exportPage,
          limit: exportLimit,
        });

        if (exportPage === 1) totalItems = res.total || 0;
        const batch = res.items || [];
        all.push(...batch);
        // USC-BUG-0036 — stop on the total, never on "fewer rows than asked for": the
        // server caps a page at 200 (and skips by the capped size, so pages stay
        // contiguous), which ended the export after 200 of 7,563 rows. An empty page
        // also ends it, in case rows were deleted mid-export.
        if (all.length >= totalItems || batch.length === 0) break;
        exportPage += 1;
      }

      const header = [
        'Name',
        'Source',
        'Available units',
        'Expiry Date',
        'Category',
        'Sub Category',
        'Packaging',
        'Unit',
        'Item Price (TT)',
      ];

      const rows = all.map((it) => [
        it.name || '',
        sourceLabel(it.source),
        String(getAvailableQty(it)),
        formatExpiry(it.expiryDate),
        it.category || '',
        it.subCategory || '',
        it.packaging || '',
        it.unit || '',
        formatMoney((it as any)?.pricing?.priceTT ?? (it as any)?.priceTT),
      ]);

      // toCsv, not a private escaper — see utils/csv. (TECH_DEBT #38)
      const csv = toCsv([header, ...rows]);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_${division}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setActionMsg({ type: 'success', text: `Exported ${all.length} item(s).` });
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e?.message || 'Export failed.' });
    } finally {
      setExportBusy(false);
    }
  };

  const openUpload = () => {
    setUploadFile(null);
    setUploadPayment(EMPTY_PAYMENT_DRAFT);
    setUploadMsg(null);
    setIsUploadOpen(true);
  };

  const closeUpload = () => {
    if (uploadBusy) return;
    setIsUploadOpen(false);
    setUploadFile(null);
    setUploadPayment(EMPTY_PAYMENT_DRAFT);
    setUploadMsg(null);
  };

  const uploadIsPdf = Boolean(
    uploadFile && (uploadFile.type === 'application/pdf' || uploadFile.name.toLowerCase().endsWith('.pdf'))
  );

  const handleUpload = async () => {
    if (!uploadFile) {
      setUploadMsg('Please select a CSV or PDF file.');
      return;
    }
    const isPdf = uploadFile.type === 'application/pdf' || uploadFile.name.toLowerCase().endsWith('.pdf');

    if (isPdf) {
      // Asked first, with the same rule the server applies. The amount of a part
      // payment is typed on the invoice's review screen, once it has been read.
      const payment = checkPaymentDraft(uploadPayment, { includeAmount: false, dateRefused: !uploadPaidDateValid });
      if (!payment.ok) {
        setUploadMsg(payment.message);
        return;
      }
      setUploadBusy(true);
      setUploadMsg(null);
      try {
        await uploadVendorInvoiceSingle({ file: uploadFile, payment: payment.input });
        setUploadMsg('Vendor invoice uploaded. Inventory will update after processing.');
        await load({ page });
        setTimeout(() => closeUpload(), 1200);
      } catch (e: any) {
        setUploadMsg(e?.message || 'PDF upload failed.');
      } finally {
        setUploadBusy(false);
      }
      return;
    }

    const text = await uploadFile.text();
    const rows = parseCsv(text);
    if (!rows.length) {
      setUploadMsg('Empty CSV file.');
      return;
    }

    // One mapper, at module scope, so what this button posts is the thing the
    // tests assert on (TECH_DEBT #142).
    const { payloads, missingColumns, issues } = mapInventoryCsvRows(rows);
    if (missingColumns.length) {
      setUploadMsg(`Missing required column(s): ${missingColumns.join(', ')}`);
      return;
    }
    // Nothing is created while a row states a pack fact this path cannot read.
    // Importing the rest and dropping the bad column would leave the operator
    // with items that look complete and are not.
    if (issues.length) {
      const shown = issues.slice(0, 3).map((i) => i.message);
      const more = issues.length > shown.length ? ` (+${issues.length - shown.length} more)` : '';
      setUploadMsg(`${shown.join(' ')}${more} Nothing was uploaded.`);
      return;
    }
    if (!payloads.length) {
      setUploadMsg('No valid rows found. Make sure the "name" column has values.');
      return;
    }

    setUploadBusy(true);
    setUploadMsg(null);

    try {
      for (const p of payloads) {
        await createInventoryItem(p);
      }
      setUploadMsg(`Uploaded ${payloads.length} item(s).`);
      await load({ page });
      setTimeout(() => closeUpload(), 800);
    } catch (e: any) {
      setUploadMsg(pantryRefusedMessage(e) || e?.message || 'Upload failed.');
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header row */}
      <div className="flex items-start justify-between gap-6 mb-3">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">
              {isPantry ? 'Pantry Inventory' : 'Ship Chandlery Inventory'}
            </h1>
            <ModuleInfoButton content={inventoryInfoContent(isPantry)} ariaLabel="Inventory information" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {!isPantry && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Source</span>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="h-7 px-2 rounded border border-slate-200 bg-white text-xs text-slate-700"
              >
                <option value="all">All</option>
                <option value="manual">Manual</option>
                <option value="invoice">Invoice</option>
              </select>
            </div>
            )}

            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Category</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="h-7 px-2 rounded border border-slate-200 bg-white text-xs text-slate-700"
              >
                <option value="all">All</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Expiry</span>
              <select
                value={expiryFilter}
                onChange={(e) => setExpiryFilter(e.target.value)}
                className="h-7 px-2 rounded border border-slate-200 bg-white text-xs text-slate-700"
              >
                <option value="all">All</option>
                <option value="expiring">Expiring soon</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Stock</span>
              <select
                value={lowStockFilter}
                onChange={(e) => setLowStockFilter(e.target.value)}
                className="h-7 px-2 rounded border border-slate-200 bg-white text-xs text-slate-700"
              >
                <option value="all">All</option>
                <option value="low">Low stock</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          {/* Search */}
          <SearchInput value={query} onChange={setQuery} placeholder="Search here" className="w-[220px]" />

          {/* Buttons */}
          <Button onClick={handleExport} disabled={exportBusy}>
            {exportBusy ? 'Exporting...' : 'Export'}
          </Button>

          {!isPantry && (
          <Button onClick={openUpload}>Upload file</Button>
          )}

          {!isPantry && (
          <Button variant="primary" onClick={openAdd} icon={<Plus aria-hidden="true" className="h-4 w-4" />}>
            Item
          </Button>
          )}
        </div>
      </div>

      {err && <div className="mb-2 text-xs text-red-600">{err}</div>}

      {/* Toast */}
      {actionMsg && (
        <div className="fixed inset-0 z-[90] flex items-start justify-center pt-6 pointer-events-none">
          <div
            className={`pointer-events-auto px-5 py-3 rounded-md text-xs font-semibold shadow-lg border flex items-center gap-3 ${
              actionMsg.type === 'success'
                ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            <span>{actionMsg.text}</span>
            <button
              onClick={() => {
                setActionMsg(null);
              }}
              className="text-slate-500 hover:text-slate-800"
              aria-label="Close message"
            >
              <IconX size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-auto">
          <table className="data-table min-w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-700">
                <th className="w-10 px-3 py-2 border-b border-slate-200">#</th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Item Name</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Source</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Available units</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Expiry Date</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Category</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Sub Category</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Packaging</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Unit</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="px-3 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>Item Price (TT)</span>
                    <button
                      type="button"
                      onClick={() => setShowColumnFilters((v) => !v)}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Toggle column filters"
                    >
                      <IconFilter />
                    </button>
                  </div>
                </th>

                <th className="w-20 px-3 py-2 border-b border-slate-200"></th>
              </tr>
              {showColumnFilters && (
                <tr className="text-left text-xs text-slate-600 bg-white">
                  <th className="px-3 py-2 border-b border-slate-200"></th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Filter"
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs"
                    />
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    {!isPantry && (
                    <select
                      value={sourceFilter}
                      onChange={(e) => setSourceFilter(e.target.value)}
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs bg-white"
                    >
                      <option value="all">All</option>
                      <option value="manual">Manual</option>
                      <option value="invoice">Invoice</option>
                    </select>
                    )}
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <input
                      value={minQtyFilter}
                      onChange={(e) => setMinQtyFilter(e.target.value)}
                      placeholder="Min"
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs"
                    />
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <select
                      value={expiryFilter}
                      onChange={(e) => setExpiryFilter(e.target.value)}
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs bg-white"
                    >
                      <option value="all">All</option>
                      <option value="expired">Expired</option>
                      <option value="expiring">Expiring</option>
                      <option value="valid">Valid</option>
                    </select>
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs bg-white"
                    >
                      <option value="all">All</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <select
                      value={subCategoryFilter}
                      onChange={(e) => setSubCategoryFilter(e.target.value)}
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs bg-white"
                    >
                      <option value="all">All</option>
                      {subCategories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <input
                      value={packagingFilter}
                      onChange={(e) => setPackagingFilter(e.target.value)}
                      placeholder="Filter"
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs"
                    />
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <input
                      value={unitFilter}
                      onChange={(e) => setUnitFilter(e.target.value)}
                      placeholder="Filter"
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs"
                    />
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200">
                    <input
                      value={minPriceFilter}
                      onChange={(e) => setMinPriceFilter(e.target.value)}
                      placeholder="Min"
                      className="w-full h-7 px-2 rounded border border-slate-200 text-xs"
                    />
                  </th>
                  <th className="px-3 py-2 border-b border-slate-200"></th>
                </tr>
              )}
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="px-4 py-6 text-center text-slate-500">
                    <LoadingState text="Loading inventory..." />
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((it, idx) => (
                  <tr
                    key={String(it._id || it.id)}
                    className={`border-b border-slate-100 last:border-b-0 ${
                      getExpiryInfo(it).isExpired || getExpiryInfo(it).isExpiring ? 'bg-red-50' : ''
                    }`}
                  >
                    <td className="px-3 py-2 text-slate-600">{idx + 1}</td>
                    <td className="px-3 py-2 text-slate-700">
                      <button
                        onClick={() => openAuditTrail(it)}
                        className="text-left font-medium text-cyan-700 underline decoration-cyan-200 underline-offset-2 hover:text-cyan-900"
                        title="Open audit trail"
                      >
                        {it.name}
                      </button>
                      {/* TECH_DEBT #52 — a supplier billed this product in
                          packaging the row disagrees with. The row was kept
                          (DECISIONS.md D1); this says so, rather than the row
                          silently changing under whoever reconciles it. */}
                      {packagingConflictsOf(it).length > 0 && (
                        <span
                          className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800 align-middle"
                          title={packagingConflictsOf(it)
                            .map((c) => c.message || `${PACK_FACT_LABELS[String(c.fact)] || c.fact}: ${c.itemValue} vs ${c.invoiceValue}`)
                            .join('\n')}
                        >
                          Packaging query
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{sourceLabel(it.source)}</td>
                    {/*
                      Both levels, the same reading the audit modal has shown since
                      TECH_DEBT #50 — "12 PACK / 300 PCS", not a bare 300.

                      USC-BUG-0047 (prod, Balgo 505636): 12 packs of Maggi cubes at 25
                      cubes a pack read as "300" here, and the client asked whether the
                      warehouse had been told 12 packs or 300. Both figures were right;
                      only one was on screen, and it was the one that does not match the
                      paper. #50 fixed exactly this complaint in the modal and left the
                      list — the screen people actually scan — printing the level-1 count
                      alone. The helper is already in this file and already tested, so
                      this is the file's own canonical call rather than a second spelling.

                      `getAvailableQty` stays: the min-quantity filter and the export
                      need one number to compare and to put in a cell.
                    */}
                    <td className="px-3 py-2 text-slate-700">
                      {formatBothLevels(getStockLevels(it).level2, getStockLevels(it).level1, (it as any)?.uom)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <DateInput validity="immediate"
                        value={inputDateValue(it.expiryDate)}
                        commit="blur"
                        onChange={(v) => updateExpiry(String(it._id || it.id), v)}
                        className="h-7 px-2 rounded border border-slate-200 bg-white text-xs text-slate-700"
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-700">{it.category || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{it.subCategory || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{it.packaging || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{it.unit || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {formatMoney((it as any)?.pricing?.priceTT ?? (it as any)?.priceTT)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-3 text-slate-600">
                        {/* USC-BUG-0037 — a pantry (Yelo sync) row is read-only, so it
                            gets no Edit control rather than a disabled one that says
                            nothing; openEdit keeps its pantry guard as a backstop. */}
                        {String(it.source || '').toLowerCase() !== 'pantry' && (
                          <button
                            onClick={() => handleEdit(it)}
                            className="hover:text-slate-900 transition"
                            aria-label="Edit item"
                            title="Edit"
                          >
                            <IconEdit />
                          </button>
                        )}

                        <button
                          onClick={() => requestDelete(String(it._id || it.id))}
                          className="hover:text-slate-900 transition"
                          aria-label="Delete item"
                          title="Delete"
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-slate-500">
                    No inventory items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination page={page} pageSize={limit} total={total} onPageChange={(next) => load({ page: next })} noun="items" />

      {/* Add Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 modal-overlay-safe">
          <div className="absolute inset-0 bg-black/40" onClick={closeAdd} aria-hidden="true" />

          <div className="relative z-[70] w-full max-w-[520px] bg-white rounded-md shadow-xl border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-900">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              <button
                onClick={closeAdd}
                className="text-slate-500 hover:text-slate-800 transition"
                aria-label="Close"
              >
                <IconX />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3 overflow-y-auto min-h-0">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Item Name</label>
                  <input
                    value={fItemName}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFItemName(next);
                      if (!next.trim()) setFieldError('itemName', 'Item name is required.');
                      else setFieldError('itemName');
                    }}
                    placeholder="Enter Item Name"
                    className={`w-full h-9 px-3 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 ${
                      formErrors.itemName ? 'border border-red-300' : 'border border-slate-200'
                    } focus:border-cyan-200`}
                  />
                  {formErrors.itemName && (
                    <div className="mt-1 text-xs text-red-600">{formErrors.itemName}</div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Expiry Date</label>
                  <DateInput
                    value={fExpiry}
                    onChange={(v) => {
                      setFExpiry(v);
                      setFieldError('expiryDate');
                    }}
                    validity={(ok) => {
                      setFExpiryValid(ok);
                      if (ok) setFieldError('expiryDate');
                    }}
                    className={`w-full h-9 px-3 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 ${
                      formErrors.expiryDate ? 'border border-red-300' : 'border border-slate-200'
                    } focus:border-cyan-200`}
                  />
                  {formErrors.expiryDate && (
                    <div className="mt-1 text-xs text-red-600">{formErrors.expiryDate}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
                  <input
                    value={fCategory}
                    onChange={(e) => setFCategory(e.target.value)}
                    placeholder="e.g. Household"
                    className="w-full h-9 px-3 border border-slate-200 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Sub Category</label>
                  <input
                    value={fSubCategory}
                    onChange={(e) => setFSubCategory(e.target.value)}
                    placeholder="e.g. Cleaning"
                    className="w-full h-9 px-3 border border-slate-200 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Packaging</label>
                  <input
                    value={fPackaging}
                    onChange={(e) => setFPackaging(e.target.value)}
                    placeholder="e.g. CASE"
                    className="w-full h-9 px-3 border border-slate-200 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Unit</label>
                  <input
                    value={fUnit}
                    onChange={(e) => setFUnit(e.target.value)}
                    placeholder="e.g. PCS"
                    className="w-full h-9 px-3 border border-slate-200 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-200"
                  />
                  <div className="mt-1 text-xs text-slate-500">
                    How the goods are counted (PCS, EA…) — not their size. A size goes below.
                  </div>
                </div>
              </div>

              {/* "14 units of 320 G each" — Units per Case is uom.level1PerLevel2,
                  Weight per Unit is uom.weightPerLevel1/weightUnit. Both used to be
                  invisible here: a case-priced, weight-labelled invoice line had no
                  field on this form to show the piece count or the piece size. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Units per {fPackaging.trim() || 'Case'}
                  </label>
                  <input
                    value={fUnitsPerCase}
                    onChange={(e) => {
                      const next = normalizeDecimalInput(e.target.value);
                      setFUnitsPerCase(next);
                      if (next && !(Number(next) > 0)) {
                        setFieldError('unitsPerCase', 'Must be a positive number.');
                      } else {
                        setFieldError('unitsPerCase');
                      }
                    }}
                    placeholder="e.g. 14"
                    inputMode="decimal"
                    className={`w-full h-9 px-3 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 ${
                      formErrors.unitsPerCase ? 'border border-red-300' : 'border border-slate-200'
                    } focus:border-cyan-200`}
                  />
                  {formErrors.unitsPerCase && (
                    <div className="mt-1 text-xs text-red-600">{formErrors.unitsPerCase}</div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Weight per Unit</label>
                  <div className="flex gap-2">
                    <input
                      value={fWeightAmount}
                      onChange={(e) => {
                        const next = normalizeDecimalInput(e.target.value);
                        setFWeightAmount(next);
                        if (next && !(Number(next) > 0)) {
                          setFieldError('weightAmount', 'Must be a positive number.');
                        } else {
                          setFieldError('weightAmount');
                        }
                      }}
                      placeholder="e.g. 320"
                      inputMode="decimal"
                      className={`flex-1 h-9 px-3 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 ${
                        formErrors.weightAmount ? 'border border-red-300' : 'border border-slate-200'
                      } focus:border-cyan-200`}
                    />
                    <select
                      value={fWeightUnit}
                      onChange={(e) => setFWeightUnit(e.target.value as WeightUnitOption)}
                      className="w-20 h-9 px-2 border border-slate-200 rounded-md text-xs bg-white outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-200"
                    >
                      {WEIGHT_UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formErrors.weightAmount && (
                    <div className="mt-1 text-xs text-red-600">{formErrors.weightAmount}</div>
                  )}
                </div>
              </div>
              {fUnitsPerCase.trim() && Number(fUnitsPerCase) > 0 && (
                <div className="text-xs text-slate-500 -mt-2">
                  {fWeightAmount.trim() && Number(fWeightAmount) > 0
                    ? `= ${Number(fUnitsPerCase)} ${(fUnit.trim() || 'PCS').toUpperCase()} of ${fWeightAmount}${fWeightUnit} each per ${(fPackaging.trim() || 'CASE').toUpperCase()}`
                    : `= ${Number(fUnitsPerCase)} ${(fUnit.trim() || 'PCS').toUpperCase()} per ${(fPackaging.trim() || 'CASE').toUpperCase()}`}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Item Price (TT)</label>
                  <input
                    value={fTT}
                    onChange={(e) => {
                      const next = normalizeDecimalInput(e.target.value);
                      setFTT(next);
                      if (next && Number.isNaN(Number(next))) {
                        setFieldError('priceTT', 'Must be a number.');
                      } else {
                        setFieldError('priceTT');
                      }
                    }}
                    placeholder="e.g. 299"
                    inputMode="decimal"
                    className={`w-full h-9 px-3 rounded-md text-xs outline-none focus:ring-2 focus:ring-cyan-100 ${
                      formErrors.priceTT ? 'border border-red-300' : 'border border-slate-200'
                    } focus:border-cyan-200`}
                  />
                  {formErrors.priceTT && (
                    <div className="mt-1 text-xs text-red-600">{formErrors.priceTT}</div>
                  )}
                  <div className="mt-1 text-xs text-slate-500">
                    Per {(fUnit.trim() || 'PCS').toUpperCase()} — not per {(fPackaging.trim() || 'CASE').toUpperCase()}.
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={closeAdd}
                className="px-4 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={addItem}
                disabled={savingItem}
                className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-60"
              >
                {savingItem ? 'Saving…' : editingItem ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadOpen && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 modal-overlay-safe">
          <div className="absolute inset-0 bg-black/40" onClick={closeUpload} aria-hidden="true" />

          <div className="relative z-[70] w-full max-w-[520px] bg-white rounded-md shadow-xl border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-900">Upload Inventory (CSV/PDF)</h2>
              <button
                onClick={closeUpload}
                className="text-slate-500 hover:text-slate-800 transition"
                aria-label="Close"
              >
                <IconX />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              {/* The template, and the only place it is written down. Keep this
                  list and `CSV_COLUMN_ALIASES` in step — a column nobody is told
                  about is the same as no column (TECH_DEBT #142). */}
              <div className="text-xs text-slate-600 space-y-1">
                <div>
                  Upload CSV or PDF. CSV requires: <span className="font-medium">name</span>. Optional: category,
                  sub category, packaging, unit, price tt, expiry date, source,{' '}
                  <span className="font-medium">units per case</span>,{' '}
                  <span className="font-medium">weight per unit</span>,{' '}
                  <span className="font-medium">weight unit</span> (G/KG/LB/OZ).
                </div>
                <div className="text-xs text-slate-500">
                  "14 units of 320 G each" is <em>units per case</em> 14, <em>weight per unit</em> 320,{' '}
                  <em>weight unit</em> G. Leave units per case out and the item is stored as 1 per pack, exactly as
                  before. A weight per unit without its weight unit is refused rather than assumed.
                </div>
              </div>

              <input
                type="file"
                accept=".csv,.pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-700"
              />

              {uploadIsPdf ? (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <PaymentOnReceiptFields
                    draft={uploadPayment}
                    onChange={setUploadPayment}
                    onDateValidityChange={setUploadPaidDateValid}
                    showAmount={false}
                    disabled={uploadBusy}
                    idPrefix="inventory-upload"
                  />
                </div>
              ) : null}

              {uploadMsg && <div className="text-xs text-slate-600">{uploadMsg}</div>}
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={closeUpload}
                className="px-4 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploadBusy}
                className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-50"
              >
                {uploadBusy ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 modal-overlay-safe">
          <div className="absolute inset-0 bg-black/40" onClick={cancelDelete} aria-hidden="true" />

        <div className="relative z-[70] w-full max-w-[360px] bg-white rounded-md shadow-xl border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200 text-sm font-semibold text-slate-900">
              Delete Item
            </div>
            <div className="px-5 py-4 text-xs text-slate-600">
              Are you sure you want to delete this inventory item?
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-[#d24b1c] text-white text-xs font-semibold hover:bg-[#bf3f14]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Modal */}
      {auditItem && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 modal-overlay-safe">
          <div className="absolute inset-0 bg-black/40" onClick={closeAuditTrail} aria-hidden="true" />

          <div className="relative z-[70] w-full max-w-[1100px] bg-white rounded-md shadow-xl border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Inventory Audit Trail</h2>
                <div className="text-xs text-slate-500">{auditItem.name}</div>
              </div>
              <button
                onClick={closeAuditTrail}
                className="text-slate-500 hover:text-slate-800 transition"
                aria-label="Close"
              >
                <IconX />
              </button>
            </div>

            <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs border-b border-slate-200 bg-slate-50">
              <div>
                <div className="text-xs text-slate-500">Source</div>
                <div className="font-semibold text-slate-800">{sourceLabel(auditItem.source)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Available Qty</div>
                {/* Both levels: the case count is the figure that reconciles
                    against the invoice's Quantity column (TECH_DEBT #50). */}
                <div className="font-semibold text-slate-800">
                  {formatBothLevels(
                    getStockLevels(auditItem).level2,
                    getStockLevels(auditItem).level1,
                    (auditItem as any)?.uom
                  )}
                </div>
                {/* Only when the row says what its level-2 unit IS. "24 per case"
                    on a row whose level2Name is missing invents the case, and
                    this screen exists because an invented unit ("OZ" lifted out
                    of a size string) made a correct count read as a weight. */}
                {/* And not for a measure pack at the seeded 1 (`isSeededMeasurePack`, the list's own
                    condition): "88 LBS" over "1 per LBS" says nothing a pound does not. */}
                {!isSeededMeasurePack((auditItem as any)?.uom) &&
                  (num((auditItem as any)?.uom?.level1PerLevel2) ?? 0) > 0 &&
                  String((auditItem as any)?.uom?.level2Name || '').trim() && (
                    <div className="text-xs text-slate-400">
                      {num((auditItem as any).uom.level1PerLevel2)} per{' '}
                      {String((auditItem as any).uom.level2Name).trim()}
                    </div>
                  )}
              </div>
              <div>
                <div className="text-xs text-slate-500">Expiry</div>
                <div className="font-semibold text-slate-800">{formatExpiry((auditItem as any)?.expiryDate || null)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Inventory ID</div>
                <div className="font-semibold text-slate-800 break-all">{String(auditItem._id || auditItem.id || '--')}</div>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-5 space-y-4">
              {/* TECH_DEBT #52 — this modal is where the drift was SEEN: a header
                  in one unit and layers in another, with nothing saying why. The
                  header no longer moves (DECISIONS.md D1), and the supplier claim
                  that lost is stated here so the two readings below stop being a
                  mystery. Settling it is an edit to the item, which clears the
                  entry for whatever fact was corrected. */}
              {packagingConflictsOf(auditItem).length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Packaging query
                  </div>
                  <div className="mt-0.5 text-xs text-amber-900">
                    A supplier billed this product in packaging this item does not agree with. The
                    item was left as it stands — edit it if the supplier is right.
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-amber-900">
                    {packagingConflictsOf(auditItem).map((c, i) => (
                      <li key={`${c.fact}-${c.invoiceNumber}-${i}`} className="flex flex-wrap gap-x-2">
                        <span className="font-semibold">
                          {PACK_FACT_LABELS[String(c.fact)] || c.fact}:
                        </span>
                        <span>
                          this item says <strong>{c.itemValue || '--'}</strong>
                          {c.invoiceNumber ? `, invoice ${c.invoiceNumber}` : ', a later invoice'}
                          {c.lineNumber != null ? ` line ${c.lineNumber}` : ''} says{' '}
                          <strong>{c.invoiceValue || '--'}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!auditLoading && (vesselBreakdown.perVesselArray.length > 0 || vesselBreakdown.totalAllocated > 0) && (
                <div className="rounded-md border border-primary-500/30 bg-primary-500/5 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold text-primary-500 uppercase tracking-wide">Multi-Vessel Allocation</div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        Units allocated to specific vessels (held in warehouse against future delivery).
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 uppercase">Total Allocated</div>
                      <div className="text-lg font-bold text-primary-500">{vesselBreakdown.totalAllocated}</div>
                      <div className="text-xs text-slate-500">
                        Free: {Math.max(0, getAvailableQty(auditItem) - vesselBreakdown.totalAllocated)}
                      </div>
                    </div>
                  </div>

                  {vesselBreakdown.perVesselArray.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {vesselBreakdown.perVesselArray.map((row) => (
                        <div
                          key={row.vessel}
                          className="rounded border border-primary-500/20 bg-white px-3 py-2 flex items-center justify-between"
                        >
                          <div className="text-xs font-medium text-slate-800 truncate" title={row.vessel}>
                            {row.vessel}
                          </div>
                          <div className="text-sm font-semibold text-primary-500">{row.qty}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* E7 — Super-admin HOLD control */}
              {!auditLoading && auditItem && (
                <div className="rounded-md border border-rose-200 bg-rose-50/40 p-4 flex flex-wrap items-center gap-2">
                  <div className="text-xs font-semibold text-rose-900 uppercase tracking-wide">HOLD</div>
                  <div className="text-xs text-slate-600">
                    Current hold: <strong>{Number((auditItem as any)?.superAdminHold?.pantryHoldLevel1 || 0)}</strong> units
                    {(auditItem as any)?.superAdminHold?.reason ? ` — ${(auditItem as any).superAdminHold.reason}` : ''}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const qtyStr = window.prompt('Hold how many units (level1)?', '0');
                      const level1 = Number(qtyStr);
                      if (!Number.isFinite(level1) || level1 <= 0) return;
                      const reason = window.prompt('Reason for hold?') || '';
                      try {
                        await setHold(String(auditItem._id || auditItem.id), { level1, reason });
                        await openAuditTrail(auditItem);
                        await load({ page });
                      } catch (err: any) { window.alert(err?.message || 'Request failed'); }
                    }}
                    className="px-2 py-1 text-xs rounded bg-rose-600 text-white hover:bg-rose-700"
                  >
                    Place Hold
                  </button>
                  {(Number((auditItem as any)?.superAdminHold?.pantryHoldLevel1 || 0) > 0) && (
                    <>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Clear the current hold?')) return;
                          try {
                            await clearHold(String(auditItem._id || auditItem.id));
                            await openAuditTrail(auditItem);
                            await load({ page });
                          } catch (err: any) { window.alert(err?.message || 'Request failed'); }
                        }}
                        className="px-2 py-1 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Confirm hold? This will DEDUCT the held qty from pantry stock.')) return;
                          try {
                            await confirmHold(String(auditItem._id || auditItem.id));
                            await openAuditTrail(auditItem);
                            await load({ page });
                          } catch (err: any) { window.alert(err?.message || 'Request failed'); }
                        }}
                        className="px-2 py-1 text-xs rounded border border-primary-500 bg-primary-500 hover:bg-primary-600 text-white"
                      >
                        Confirm (Deduct)
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* E3 — Pantry ↔ USC transfer control */}
              {!auditLoading && (
                <div className="rounded-md border border-violet-200 bg-violet-50/40 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-violet-900 uppercase tracking-wide">Pantry ⇄ USC Transfer</div>
                      <div className="text-xs text-slate-600 mt-0.5">Move stock between Pantry and United warehouse. Creates a TRANSFER movement + updates both balances.</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="text-xs text-slate-500 uppercase block">Direction</label>
                      <select
                        value={transferDirection}
                        onChange={(e) => setTransferDirection(e.target.value as any)}
                        className="border border-slate-300 rounded text-xs px-2 py-1.5"
                      >
                        <option value="united_to_pantry">United → Pantry</option>
                        <option value="pantry_to_united">Pantry → United</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase block">Qty (level 1)</label>
                      <input
                        type="number" min={0} step="1"
                        placeholder="0"
                        value={transferQty}
                        onChange={(e) => setTransferQty(e.target.value)}
                        className="w-24 border border-slate-300 rounded text-xs px-2 py-1.5"
                      />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-xs text-slate-500 uppercase block">Note (optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. Restock for Ship A run"
                        value={transferNote}
                        onChange={(e) => setTransferNote(e.target.value)}
                        className="w-full border border-slate-300 rounded text-xs px-2 py-1.5"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={submitPantryTransfer}
                      disabled={transferBusy}
                      className="px-3 py-1.5 text-xs font-semibold rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                    >
                      {transferBusy ? 'Transferring…' : 'Transfer'}
                    </button>
                  </div>
                </div>
              )}

              {/* D4 — Pull retained units to next ship */}
              {!auditLoading && vesselBreakdown.perVesselArray.length > 0 && (
                <div className="rounded-md border border-sky-200 bg-sky-50/40 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-sky-900 uppercase tracking-wide">Pull Retained → Next Ship</div>
                      <div className="text-xs text-slate-600 mt-0.5">Reallocate units held for one vessel to another. Records a return + a fresh allocation atomically.</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="text-xs text-slate-500 uppercase block">From vessel</label>
                      <select
                        value={pullFromVessel}
                        onChange={(e) => setPullFromVessel(e.target.value)}
                        className="border border-slate-300 rounded text-xs px-2 py-1.5 min-w-[160px]"
                      >
                        <option value="">— pick —</option>
                        {vesselBreakdown.perVesselArray.map((v) => (
                          <option key={v.vessel} value={v.vessel}>{v.vessel} ({v.qty})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase block">To vessel</label>
                      <input
                        type="text"
                        placeholder="e.g. Maersk Lines"
                        value={pullToVessel}
                        onChange={(e) => setPullToVessel(e.target.value)}
                        className="border border-slate-300 rounded text-xs px-2 py-1.5 min-w-[160px]"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase block">Qty</label>
                      <input
                        type="number" min={0} step="1"
                        placeholder="0"
                        value={pullQty}
                        onChange={(e) => setPullQty(e.target.value)}
                        className="w-20 border border-slate-300 rounded text-xs px-2 py-1.5"
                      />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <label className="text-xs text-slate-500 uppercase block">Reason (opt.)</label>
                      <input
                        type="text"
                        placeholder="e.g. Ship A cancelled last-min order"
                        value={pullReason}
                        onChange={(e) => setPullReason(e.target.value)}
                        className="w-full border border-slate-300 rounded text-xs px-2 py-1.5"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={submitPullRetained}
                      disabled={pullBusy}
                      className="px-3 py-1.5 text-xs font-semibold rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                    >
                      {pullBusy ? 'Pulling…' : 'Pull Units'}
                    </button>
                  </div>
                </div>
              )}

              {/* D3 — Real case-break: open N sealed cases at a location and
                  convert them into N × level1PerLevel2 loose units at the
                  SAME location. Distinct from the "Split Across Vessels"
                  block below (which allocates existing loose units to ships). */}
              {!auditLoading && (
                <div className="rounded-md border border-purple-200 bg-purple-50/40 p-4 mb-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-purple-900 uppercase tracking-wide">Case Break — Open Sealed Cases</div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        Convert N sealed cases into loose units at the same location. Same warehouse total; changes UOM composition. Emits a CASE_BREAK ledger row.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 uppercase">Location</label>
                    <select
                      value={caseBreakLoc}
                      onChange={(e) => setCaseBreakLoc(e.target.value as 'united' | 'pantry')}
                      className="border border-slate-300 rounded text-xs px-2 py-1"
                    >
                      <option value="united">United Stock</option>
                      <option value="pantry">Pantry</option>
                    </select>
                    <label className="text-xs text-slate-500 uppercase ml-2">Cases to break</label>
                    <input
                      type="number"
                      min={1}
                      step="1"
                      value={caseBreakCount}
                      onChange={(e) => setCaseBreakCount(e.target.value)}
                      className="w-24 border border-slate-300 rounded text-xs px-2 py-1.5"
                      placeholder="e.g. 2"
                    />
                    <input
                      type="text"
                      value={caseBreakReason}
                      onChange={(e) => setCaseBreakReason(e.target.value)}
                      className="flex-1 border border-slate-300 rounded text-xs px-2 py-1.5"
                      placeholder="Reason (optional — e.g. 'Ship needed 3 loose packs')"
                    />
                    <button
                      type="button"
                      onClick={submitCaseBreak}
                      disabled={caseBreakBusy || !caseBreakCount || Number(caseBreakCount) <= 0}
                      className="px-3 py-1.5 text-xs font-semibold rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                    >
                      {caseBreakBusy ? 'Breaking…' : 'Break Cases'}
                    </button>
                  </div>
                </div>
              )}

              {!auditLoading && (
                <div className="rounded-md border border-amber-200 bg-amber-50/40 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Case Break — Split Across Vessels</div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        Allocate units to multiple vessels in one go. Example: 10 to Ship A, 2 held in inventory for Ship B.
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase block">Source</label>
                      <select
                        value={splitFrom}
                        onChange={(e) => {
                          setSplitFrom(e.target.value as 'united' | 'pantry');
                          // The suggestions and boxes were worked out on the other shelf.
                          setSplitRows((rows) => rows.map(withoutCases));
                        }}
                        className="border border-slate-300 rounded text-xs px-2 py-1"
                      >
                        <option value="united">United Stock</option>
                        <option value="pantry">Pantry</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {splitRows.map((row, idx) => (
                      <div key={row.id} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Vessel name (e.g. Stena Promise)"
                          value={row.vessel}
                          onChange={(e) => {
                            const next = [...splitRows];
                            next[idx] = { ...next[idx], vessel: e.target.value };
                            setSplitRows(next);
                          }}
                          className="flex-1 border border-slate-300 rounded text-xs px-2 py-1.5"
                        />
                        <input
                          type="number"
                          min={0}
                          step="any"
                          placeholder="Qty"
                          value={row.qty}
                          readOnly={!!row.caseRefs?.length}
                          onChange={(e) => {
                            const next = [...splitRows];
                            next[idx] = { ...next[idx], qty: e.target.value };
                            setSplitRows(next);
                          }}
                          className={`w-24 border border-slate-300 rounded text-xs px-2 py-1.5 ${
                            row.caseRefs?.length ? 'bg-slate-100 text-slate-500' : ''
                          }`}
                        />
                        {/* Only offered on catch-weight goods. Everything else
                            has no boxes to name, and allocates by quantity. */}
                        {itemCases && itemCases.cases.length > 0 && (
                          <button
                            type="button"
                            onClick={() => suggestCases(row.id)}
                            disabled={casePlanBusy === row.id}
                            className="px-2 py-1.5 text-xs font-semibold rounded border border-primary-500 text-primary-500 hover:bg-primary-500/5 disabled:opacity-50 whitespace-nowrap"
                            title="Work out which cases to pull for this quantity"
                          >
                            {casePlanBusy === row.id ? 'Working…' : 'Suggest cases'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (splitRows.length <= 1) return;
                            setSplitRows(splitRows.filter((_, i) => i !== idx));
                          }}
                          disabled={splitRows.length <= 1}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30 text-xs px-2"
                          title="Remove row"
                        >
                          ×
                        </button>
                      </div>

                      {/* The recommendation. Cases are toggleable, because a
                          picker who finds one damaged needs to swap it and see
                          what that does to the total before committing. */}
                      {row.plan && (
                        <div className="ml-1 rounded-md border border-primary-500/30 bg-primary-500/5 px-3 py-2">
                          <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <div className="text-xs font-semibold text-primary-500">
                              {row.plan.count} case{row.plan.count === 1 ? '' : 's'} →{' '}
                              {row.plan.total} {itemCases?.uomLevel1Name || ''}
                              {row.plan.satisfied ? (
                                <span className="font-normal text-slate-500">
                                  {row.plan.overage > 0
                                    ? ` (+${row.plan.overage} over ${row.plan.targetLevel1})`
                                    : ' (exact)'}
                                </span>
                              ) : (
                                <span className="font-normal text-amber-700">
                                  {' '}
                                  — short by {row.plan.shortfall}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => acceptCasePlan(row.id)}
                                disabled={!row.plan.picks.length}
                                className="text-xs font-semibold text-primary-500 hover:underline disabled:opacity-40"
                              >
                                Use these cases
                              </button>
                              {!!row.caseRefs?.length && (
                                <button
                                  type="button"
                                  onClick={() => clearCasePlan(row.id)}
                                  className="text-xs text-slate-500 hover:underline"
                                >
                                  clear
                                </button>
                              )}
                            </div>
                          </div>

                          {!row.plan.exact && row.plan.method === 'greedy_swap' && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              Best found — too many cases on hand to prove it is the best possible.
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(itemCases?.cases || []).map((c) => {
                              const picked = row.plan!.picks.some(
                                (p) => p.layerId === c.layerId && p.seq === c.seq
                              );
                              // A box held for another order, or set aside for a
                              // vendor return (D44), is shown but not offered —
                              // "case 3 is spoken for" is useful, "case 3 does
                              // not exist" is misleading.
                              const held = c.status !== 'available';
                              return (
                                <button
                                  key={`${c.layerId}:${c.seq}`}
                                  type="button"
                                  disabled={held}
                                  onClick={() => toggleCaseInPlan(row.id, c)}
                                  title={
                                    c.status === 'held'
                                      ? `Case ${c.seq} is held for a vendor return`
                                      : held
                                      ? `Case ${c.seq} is already held${
                                          c.vesselName ? ` for ${c.vesselName}` : ''
                                        }`
                                      : c.opened
                                      ? `Case ${c.seq} — part used, ${c.weightRemainingLevel1} left of ${c.weightLevel1}`
                                      : `Case ${c.seq}${c.code ? ` (${c.code})` : ''}`
                                  }
                                  className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                                    held
                                      ? 'bg-slate-100 text-slate-400 border-slate-200 line-through cursor-not-allowed'
                                      : picked
                                      ? 'bg-primary-500 text-white border-primary-500'
                                      : 'bg-white text-slate-600 border-slate-300 hover:border-primary-500'
                                  }`}
                                >
                                  #{c.seq} {c.weightRemainingLevel1}
                                  {c.opened ? ' ✂' : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <button
                      type="button"
                      onClick={() => setSplitRows([...splitRows, newSplitRow()])}
                      className="text-xs text-primary-500 hover:underline"
                    >
                      + Add another vessel
                    </button>
                    <button
                      type="button"
                      onClick={submitVesselSplit}
                      disabled={splitBusy}
                      className="px-3 py-1.5 text-xs font-semibold rounded bg-primary-500 text-white hover:bg-[#08568b] disabled:opacity-50"
                    >
                      {splitBusy ? 'Allocating…' : 'Allocate to Vessels'}
                    </button>
                  </div>
                </div>
              )}

              {/* Stock on hand, receipt by receipt.
                  The balance alone cannot answer the two questions that matter
                  when deciding whether to sell this stock or buy fresh: how old
                  is it, and what did it actually cost. Listed in the order it
                  would be consumed — oldest expiry first, undated last. */}
              {!auditLoading && stockLayers && (
                (stockLayers.united.layers.length > 0 || stockLayers.pantry.layers.length > 0) && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-slate-700">
                        Stock on hand — by receipt
                      </h4>
                      <span className="text-xs text-slate-400">consumed top to bottom</span>
                    </div>

                    {/* Drift means stock moved without a ledger row behind it.
                        Surfaced rather than hidden: it is the only visible
                        symptom of the untracked write paths. */}
                    {stockLayers.reconciliation?.drifted && (
                      <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {(['united', 'pantry'] as const)
                          .filter((loc) => Math.abs(stockLayers.reconciliation!.locations[loc].driftLevel1) > 0.01)
                          .map((loc) => {
                            const d = stockLayers.reconciliation!.locations[loc];
                            return (
                              <div key={loc}>
                                <span className="font-semibold capitalize">{loc}</span>: balance says{' '}
                                {d.stockTotalLevel1}, receipts account for {d.layerTotalLevel1} (
                                {d.driftLevel1 > 0 ? '+' : ''}
                                {d.driftLevel1}). Some stock was changed without a movement record.
                              </div>
                            );
                          })}
                      </div>
                    )}

                    <div className="rounded-md border border-slate-200 overflow-hidden">
                      <table className="data-table min-w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="px-3 py-2 text-left">Location</th>
                            <th className="px-3 py-2 text-right">Remaining</th>
                            <th className="px-3 py-2 text-right">Reserved</th>
                            <th className="px-3 py-2 text-right">Held for return</th>
                            <th className="px-3 py-2 text-left">Expires</th>
                            <th className="px-3 py-2 text-left">Received</th>
                            <th className="px-3 py-2 text-right">Unit cost</th>
                            <th className="px-3 py-2 text-left">From invoice</th>
                            <th className="px-3 py-2 text-left">Bought for</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(['united', 'pantry'] as const).flatMap((loc) =>
                            stockLayers[loc].layers.map((layer) => (
                              <tr key={layer._id} className="border-t border-slate-100">
                                <td className="px-3 py-2 capitalize text-slate-600">{loc}</td>
                                <td className="px-3 py-2 text-right font-medium">
                                  {layer.qtyRemainingLevel1}{' '}
                                  <span className="text-slate-400 font-normal">{layer.uomLevel1Name}</span>
                                  {/* The case equivalent, because the level-1
                                      name can be a weight lifted out of the
                                      product size ("8 OZ" for a count of 8) and
                                      the invoice is written in cases. #50. */}
                                  {layerCaseCount(layer) !== null && (
                                    <span className="block text-xs text-slate-400 font-normal">
                                      {/* The name is NOT defaulted to "CASE".
                                          Inventing the level-2 unit is the same
                                          mistake as the "OZ" this screen exists
                                          to make readable. See movementLevel2.
                                          A bare count says less and claims less. */}
                                      {layerCaseCount(layer)}
                                      {String(layer.uomLevel2Name || '').trim()
                                        ? ` ${String(layer.uomLevel2Name).trim()}`
                                        : ''}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-500">
                                  {layer.qtyReservedLevel1 > 0 ? layer.qtyReservedLevel1 : '--'}
                                </td>
                                {/* Goods set aside for vendor returns (D44): still
                                    in Remaining, but no allocation can take them. */}
                                <td className="px-3 py-2 text-right text-slate-500">
                                  {layerHeldLabel(layer) ?? '--'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {layer.expiryDate ? (
                                    formatExpiry(layer.expiryDate)
                                  ) : (
                                    <span className="text-slate-400 italic">not recorded</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                                  {formatDate(layer.receivedAt)}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-600">
                                  {layer.costPerLevel1 > 0
                                    ? `${layer.currency === 'TTD' ? 'TT$' : ''}${layer.costPerLevel1.toFixed(2)}`
                                    : '--'}
                                </td>
                                <td className="px-3 py-2 text-slate-500">
                                  {layer.sourceInvoiceNumber || (layer.backfilled ? 'reconstructed' : '--')}
                                  {layer.sourceVendorName ? ` · ${layer.sourceVendorName}` : ''}
                                </td>
                                <td className="px-3 py-2 text-slate-500">
                                  {layer.originalVesselName || '--'}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}

              {/* The physical boxes behind a catch-weight balance.
                  A kg figure cannot be carried out of a cold room; boxes can.
                  Shown even when nobody is picking, because "what is actually
                  on the shelf" is the question this drawer exists to answer. */}
              {!auditLoading && itemCases && itemCases.cases.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-slate-700">Cases on hand</h4>
                    <span className="text-xs text-slate-400">
                      {itemCases.cases.length} case{itemCases.cases.length === 1 ? '' : 's'} ·{' '}
                      {Math.round(
                        itemCases.cases.reduce((s, c) => s + c.weightRemainingLevel1, 0) * 1000
                      ) / 1000}{' '}
                      {itemCases.uomLevel1Name}
                    </span>
                  </div>
                  <div className="rounded-md border border-slate-200 overflow-hidden">
                    <table className="data-table min-w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Case</th>
                          <th className="px-3 py-2 text-right">Weight</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Expires</th>
                          <th className="px-3 py-2 text-left">From invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemCases.cases.map((c) => (
                          <tr key={`${c.layerId}:${c.seq}`} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium text-slate-700">
                              #{c.seq}
                              {c.code ? (
                                <span className="text-slate-400 font-normal"> · {c.code}</span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {c.weightRemainingLevel1}{' '}
                              <span className="text-slate-400">{c.uomLevel1Name}</span>
                              {c.opened && (
                                <span
                                  className="text-amber-600"
                                  title={`Part used — ${c.weightLevel1} as received`}
                                >
                                  {' '}
                                  ✂
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 capitalize text-slate-600">
                              {c.status === 'reserved' && c.vesselName ? (
                                `held for ${c.vesselName}`
                              ) : c.status === 'held' ? (
                                // Sentence case inside the capitalised cell.
                                <span className="normal-case">Held for vendor return</span>
                              ) : (
                                c.status
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {c.expiryDate ? (
                                formatExpiry(c.expiryDate)
                              ) : (
                                <span className="text-slate-400 italic">not recorded</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-500">
                              {c.sourceInvoiceNumber || '--'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* A catch-weight receipt whose printed weights could not be
                  reconciled has no boxes, and the reason is worth saying: this
                  item is normally picked by case and this delivery cannot be. */}
              {!auditLoading && itemCases?.note && itemCases.cases.length === 0 && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {itemCases.note}
                </div>
              )}

              {auditLoading ? (
                <div className="py-10 text-center text-slate-500 text-sm">Loading movements...</div>
              ) : (
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <table className="data-table min-w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Created</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Qty</th>
                        <th className="px-3 py-2 text-left">From</th>
                        <th className="px-3 py-2 text-left">To</th>
                        <th className="px-3 py-2 text-left">Vessel</th>
                        <th className="px-3 py-2 text-left">Client Invoice</th>
                        <th className="px-3 py-2 text-left">Delivery Slip</th>
                        <th className="px-3 py-2 text-left">Note</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(auditMovements || []).length > 0 ? (
                        auditMovements.map((movement: any) => (
                          <tr key={movement._id || movement.id} className="border-t border-slate-100 align-top">
                            <td className="px-3 py-2 whitespace-nowrap">{formatDate(movement.createdAt)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{movement.type || '--'}</td>
                            {/* Both levels. The movement stores each one, so the
                                ledger can be reconciled against the paper
                                invoice from this screen (TECH_DEBT #50). */}
                            <td className="px-3 py-2 whitespace-nowrap">
                              {movement.qty?.level1Qty ?? movement.quantityLevel1 ?? '--'}
                              {movementLevel2(movement, (auditItem as any)?.uom?.level2Name) && (
                                <span className="block text-xs text-slate-400">
                                  {movementLevel2(movement, (auditItem as any)?.uom?.level2Name)}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {movement.from?.location || movement.fromLocation || '--'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {movement.to?.location || movement.toLocation || '--'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {movement.to?.shipName ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary-500/10 text-primary-500 text-xs font-medium">
                                  {movement.to.shipName}
                                </span>
                              ) : (
                                '--'
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {movement.reference?.clientInvoiceId ? (
                                <button
                                  onClick={() => navigate(`/client-invoices/${movement.reference.clientInvoiceId}`)}
                                  className="text-cyan-700 hover:text-cyan-900 underline decoration-cyan-200 underline-offset-2"
                                >
                                  {movement.reference.invoiceNumber || 'Open invoice'}
                                </button>
                              ) : (
                                '--'
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{movement.reference?.slipNumber || '--'}</td>
                            <td className="px-3 py-2 max-w-[280px]">
                              <div className="truncate" title={movement.note || ''}>
                                {movement.note || '--'}
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{movement.status ? <StatusBadge status={String(movement.status)} /> : '--'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                            No audit trail entries found for this item.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
